import React from 'react';
import UnifiedSimpleConversionExplanation from './UnifiedSimpleConversionExplanation';

const formatConversionSteps = (value, fromUnit, toUnit, formula, calculation, result) => {
  const numericValue = parseFloat(value);
  const shortFromUnit = fromUnit.toLowerCase().slice(0, 2);
  const shortToUnit = toUnit.toLowerCase().slice(0, 2);

  const numericResult = result ? result.toString().split(' ')[0] : '0';

  const conversionFactor = formula.split('×')[1]?.trim() || formula.split('÷')[1]?.trim() || '3.28084';

  const step2WithHighlight = `${shortToUnit} = <span class="text-blue-600 dark:text-blue-400">${numericValue}</span> ${formula.includes('÷') ? '÷' : '×'} ${conversionFactor}`;

  const step2Plain = `${shortToUnit} = ${numericValue} ${formula.includes('÷') ? '÷' : '×'} ${conversionFactor}`;

  return [
    `${numericValue}${shortFromUnit}`,  // Step 1: Initial value
    step2WithHighlight,  // Step 2: Substitution with blue highlight
    `${step2Plain} = ${numericResult} ${shortToUnit}`,  // Step 3: Same equation as step 2 (plain text) with result and unit
    `Final result: ${result || numericResult + ' ' + toUnit}`  // Step 4: Final result with full unit name
  ];
};

const LengthConversionExplanation = ({
  fromUnit,
  toUnit,
  inputValue,
  result,
  darkMode
}) => {
  const getConversionData = () => {
    // Set a proper example value when no result exists
    const exampleValue = inputValue || "30"; // Use inputValue if available, otherwise default to "30"
    const valueToUse = inputValue || exampleValue; // Changed this line to prevent "null" showing up

    // Metric to Metric conversions (simple case)
    if (fromUnit === 'Meters' && toUnit === 'Kilometers') {
      return {
        introduction: [
          "The meter (m) and kilometer (km) are both units in the metric system, which is based on powers of 10.",
          "This makes conversions between metric units straightforward: moving up the scale (to larger units) means dividing by powers of 10, while moving down means multiplying."
        ],
        formula: {
          main: "km = m ÷ 1000",
          alternate: "m = km × 1000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,  // Now using our properly defined valueToUse
          'm',
          'km',
          'km = m ÷ 1000',
          `${valueToUse} ÷ 1000 = ${result || '0.03'}`,  // Provide example result
          result || '0.03 km'  // Provide example result with unit
        ),
        realWorldExample: "A 5000m race (5km) is a popular running distance, known as a '5K'. This shows how we commonly switch between meters and kilometers depending on the scale we're discussing.",
        funFacts: [
          "The prefix 'kilo' means 1000 in the metric system",
          "Other metric prefixes follow the same pattern: 'milli' (1/1000), 'centi' (1/100), 'deci' (1/10)",
          "The metric system is used by over 95% of the world's population"
        ]
      };
    }

    // Metric to Imperial (complex case)
    if (fromUnit === 'Meters' && toUnit === 'Feet') {
      return {
        introduction: [
          "Converting between meters and feet connects the metric system with the imperial system.",
          "While these systems developed independently, they've been precisely related through international standards."
        ],
        fundamentalEquality: {
          statement: "1 meter = 3.28084 feet",
          explanation: "This standardized relationship makes conversion between systems possible"
        },
        formula: {
          main: "ft = m × 3.28084",
          alternate: "m = ft ÷ 3.28084"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'm',
          'ft',
          'ft = m × 3.28084',
          `${valueToUse} × 3.28084 = ${result || '98.42519685'}`,
          result || '98.42519685 ft'
        ),
        realWorldExample: "A basketball hoop is 10 feet (about 3.05 meters) high. Professional basketball players can jump high enough to reach more than 12 feet (3.66 meters)!",
        funFacts: [
          "The foot was standardized in 1959 as exactly 0.3048 meters",
          "Before standardization, the length of a foot varied between countries",
          "The ancient Egyptian royal cubit, one of the earliest length units, was about 0.524 meters"
        ]
      };
    }

    // Kilometers to Miles (complex relationship)
    if (fromUnit === 'Kilometers' && toUnit === 'Miles') {
      return {
        introduction: [
          "Converting between kilometers and miles is one of the most common needs when traveling between countries that use different systems.",
          "While not a round number, the relationship between these units has been precisely defined."
        ],
        fundamentalEquality: {
          statement: "1 mile = 1.60934 kilometers",
          explanation: "This relationship is the foundation for all mile-kilometer conversions"
        },
        formula: {
          main: "mi = km ÷ 1.60934",
          alternate: "km = mi × 1.60934"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'km',
          'mi',
          'mi = km ÷ 1.60934',
          `${valueToUse} ÷ 1.60934 = ${result || '18.64113577'}`,
          result || '18.64113577 mi'
        ),
        realWorldExample: "A marathon is exactly 42.195 kilometers or 26.219 miles. Many runners find it helpful to know that 5 kilometers is approximately 3.1 miles when training.",
        funFacts: [
          "The mile comes from the Latin 'mille passus' meaning 'thousand paces'",
          "A commonly used rough conversion is that 8 kilometers is about 5 miles",
          "The original Roman mile was about 1,480 meters, shorter than today's mile"
        ]
      };
    }

    // Add this inside getConversionData after the existing Meters to Feet conversion
    if (fromUnit === 'Feet' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting from feet to meters connects the imperial system with the metric system.",
          "This conversion is common in international contexts, especially in engineering and construction."
        ],
        fundamentalEquality: {
          statement: "1 foot = 0.3048 meters",
          explanation: "This standardized relationship allows precise conversion between systems"
        },
        formula: {
          main: "m = ft × 0.3048",
          alternate: "ft = m ÷ 0.3048"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ft',
          'm',
          'm = ft × 0.3048',
          `${valueToUse} × 0.3048 = ${result || '9.144'}`,
          result || '9.144 m'
        ),
        realWorldExample: "Many swimming pools are 50 feet long, which is approximately 15.24 meters. Olympic pools, however, are exactly 50 meters in length.",
        funFacts: [
          "The foot was originally based on the length of a human foot",
          "The metric system was created during the French Revolution to standardize measurements",
          "NASA lost a Mars orbiter due to confusion between metric and imperial units"
        ]
      };
    }

    // Miles to Kilometers
    if (fromUnit === 'Miles' && toUnit === 'Kilometers') {
      return {
        introduction: [
          "Converting between miles and kilometers is essential when working with different measurement systems.",
          "The relationship between these units is precisely defined through international standards."
        ],
        fundamentalEquality: {
          statement: "1 mile = 1.60934 kilometers",
          explanation: "This relationship is the foundation for all mile-kilometer conversions"
        },
        formula: {
          main: "km = mi × 1.60934",
          alternate: "mi = km ÷ 1.60934"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mi',
          'km',
          'km = mi × 1.60934',
          `${valueToUse} × 1.60934 = ${result || '48.28020'}`,
          result || '48.28020 km'
        ),
        realWorldExample: "A marathon is exactly 26.219 miles or 42.195 kilometers. Runners often need to convert between these units during international events.",
        funFacts: [
          "The mile was originally defined as 1000 paces of a Roman legion",
          "A quick estimation: multiply miles by 1.6 to get approximate kilometers",
          "The nautical mile is different from the standard mile and is based on the Earth's circumference"
        ]
      };
    }

    // Kilometers to Meters (simple metric conversion)
    if (fromUnit === 'Kilometers' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting between kilometers and meters is straightforward as they're both metric units.",
          "In the metric system, each unit is related by powers of 10, making conversions simple."
        ],
        formula: {
          main: "m = km × 1000",
          alternate: "km = m ÷ 1000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'km',
          'm',
          'm = km × 1000',
          `${valueToUse} × 1000 = ${result || '30000'}`,
          result || '30000 m'
        ),
        realWorldExample: "A 5K race is 5 kilometers or 5000 meters. Athletes often switch between these units when discussing different racing distances.",
        funFacts: [
          "The metric system was designed to make conversions as simple as possible",
          "Moving up units (m to km) means dividing by 1000",
          "Moving down units (km to m) means multiplying by 1000"
        ]
      };
    }

    // Feet to Inches
    if (fromUnit === 'Feet' && toUnit === 'Inches') {
      return {
        introduction: [
          "Converting between feet and inches is a common need in construction and everyday measurements.",
          "These imperial units have a simple relationship: 1 foot equals 12 inches."
        ],
        fundamentalEquality: {
          statement: "1 foot = 12 inches",
          explanation: "This is one of the most basic relationships in imperial measurements"
        },
        formula: {
          main: "in = ft × 12",
          alternate: "ft = in ÷ 12"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ft',
          'in',
          'in = ft × 12',
          `${valueToUse} × 12 = ${result || '360'}`,
          result || '360 in'
        ),
        realWorldExample: "Many building materials are measured in feet and inches, such as door frames and window sills.",
        funFacts: [
          "The inch was originally defined as the width of a human thumb",
          "The foot was originally based on the length of a human foot",
          "The metric system was created during the French Revolution to standardize measurements"
        ]
      };
    }

    // Meters to Inches
    if (fromUnit === 'Meters' && toUnit === 'Inches') {
      return {
        introduction: [
          "Converting from meters to inches connects the metric system with imperial measurements.",
          "This conversion is common in international manufacturing and design."
        ],
        fundamentalEquality: {
          statement: "1 meter = 39.3701 inches",
          explanation: "This relationship allows precise conversion between metric and imperial systems"
        },
        formula: {
          main: "in = m × 39.3701",
          alternate: "m = in ÷ 39.3701"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'm',
          'in',
          'in = m × 39.3701',
          `${valueToUse} × 39.3701 = ${result || '1181.103'}`,
          result || '1181.103 in'
        ),
        realWorldExample: "TV screens are often advertised in inches worldwide, even in countries that use the metric system. A 1-meter TV screen is about 39.37 inches diagonal.",
        funFacts: [
          "The inch was standardized in 1959 as exactly 25.4 millimeters",
          "Many products are designed in metric but labeled in inches for different markets",
          "The metric system is used in scientific measurements worldwide"
        ]
      };
    }

    // Inches to Meters
    if (fromUnit === 'Inches' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting from inches to meters bridges the imperial and metric systems.",
          "This conversion is essential in international trade and engineering."
        ],
        fundamentalEquality: {
          statement: "1 inch = 0.0254 meters",
          explanation: "This precise relationship was established in 1959"
        },
        formula: {
          main: "m = in × 0.0254",
          alternate: "in = m ÷ 0.0254"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'in',
          'm',
          'm = in × 0.0254',
          `${valueToUse} × 0.0254 = ${result || '0.762'}`,
          result || '0.762 m'
        ),
        realWorldExample: "Computer monitors are typically measured in inches. A 24-inch monitor is approximately 0.61 meters diagonal.",
        funFacts: [
          "The definition of the inch in metric units is exact: 25.4 millimeters",
          "This standardization helped align manufacturing worldwide",
          "Before 1959, the British and American inches were slightly different"
        ]
      };
    }

    // Miles to Feet
    if (fromUnit === 'Miles' && toUnit === 'Feet') {
      return {
        introduction: [
          "Converting between miles and feet is common in surveying and navigation.",
          "The relationship between these imperial units is based on a fixed ratio."
        ],
        fundamentalEquality: {
          statement: "1 mile = 5280 feet",
          explanation: "This relationship has been standard since medieval times"
        },
        formula: {
          main: "ft = mi × 5280",
          alternate: "mi = ft ÷ 5280"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mi',
          'ft',
          'ft = mi × 5280',
          `${valueToUse} × 5280 = ${result || '158400'}`,
          result || '158400 ft'
        ),
        realWorldExample: "A mile-high city like Denver is 5,280 feet above sea level. This makes it easy to remember the conversion factor.",
        funFacts: [
          "The word 'mile' comes from the Latin for 'thousand paces'",
          "The 5280 feet per mile standard was established in 1593",
          "Some US property deeds still use chains (66 feet) as a unit of measure"
        ]
      };
    }

    // Feet to Miles
    if (fromUnit === 'Feet' && toUnit === 'Miles') {
      return {
        introduction: [
          "Converting from feet to miles is useful in distance measurements and geography.",
          "This conversion uses the same relationship as miles to feet, just in reverse."
        ],
        fundamentalEquality: {
          statement: "5280 feet = 1 mile",
          explanation: "This standard relationship makes conversion straightforward"
        },
        formula: {
          main: "mi = ft ÷ 5280",
          alternate: "ft = mi × 5280"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ft',
          'mi',
          'mi = ft ÷ 5280',
          `${valueToUse} ÷ 5280 = ${result || '0.005682'}`,
          result || '0.005682 mi'
        ),
        realWorldExample: "A typical running track is 1320 feet around, which is exactly a quarter mile.",
        funFacts: [
          "The modern mile was standardized in Britain by statute in 1593",
          "Before standardization, different types of miles were used in different regions",
          "The Romans used a shorter mile of 5000 feet"
        ]
      };
    }

    // Nanometers to Micrometers
    if (fromUnit === 'Nanometers' && toUnit === 'Micrometers') {
      return {
        introduction: [
          "Converting between nanometers and micrometers is common in microscopic measurements.",
          "As both are metric units, the conversion follows the standard metric system pattern."
        ],
        formula: {
          main: "μm = nm ÷ 1000",
          alternate: "nm = μm × 1000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'nm',
          'μm',
          'μm = nm ÷ 1000',
          `${valueToUse} ÷ 1000 = ${result || '0.03'}`,
          result || '0.03 μm'
        ),
        realWorldExample: "The wavelength of visible light ranges from 400 to 700 nanometers, or 0.4 to 0.7 micrometers.",
        funFacts: [
          "A nanometer is one billionth (10⁻⁹) of a meter",
          "A micrometer is one millionth (10⁻⁶) of a meter",
          "DNA double helix is about 2 nanometers in diameter"
        ]
      };
    }

    // Micrometers to Millimeters
    if (fromUnit === 'Micrometers' && toUnit === 'Millimeters') {
      return {
        introduction: [
          "Converting between micrometers and millimeters involves metric prefixes.",
          "This conversion is common in precision engineering and microscopy."
        ],
        formula: {
          main: "mm = μm ÷ 1000",
          alternate: "μm = mm × 1000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'μm',
          'mm',
          'mm = μm ÷ 1000',
          `${valueToUse} ÷ 1000 = ${result || '0.03'}`,
          result || '0.03 mm'
        ),
        realWorldExample: "A human hair is about 100 micrometers (0.1 millimeters) in diameter.",
        funFacts: [
          "The prefix 'micro' means one millionth (10⁻⁶)",
          "The prefix 'milli' means one thousandth (10⁻³)",
          "Micrometers are often called 'microns' in scientific contexts"
        ]
      };
    }

    // Millimeters to Centimeters
    if (fromUnit === 'Millimeters' && toUnit === 'Centimeters') {
      return {
        introduction: [
          "Converting between millimeters and centimeters is one of the most basic metric conversions.",
          "These units are commonly used in everyday measurements and engineering."
        ],
        formula: {
          main: "cm = mm ÷ 10",
          alternate: "mm = cm × 10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mm',
          'cm',
          'cm = mm ÷ 10',
          `${valueToUse} ÷ 10 = ${result || '3'}`,
          result || '3 cm'
        ),
        realWorldExample: "A paper clip is about 3 centimeters (30 millimeters) long.",
        funFacts: [
          "The prefix 'centi' means one hundredth (10⁻²)",
          "Millimeters are often used in technical drawings",
          "Most rulers show both centimeters and millimeters"
        ]
      };
    }

    // Centimeters to Meters
    if (fromUnit === 'Centimeters' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting between centimeters and meters is a fundamental metric conversion.",
          "Since both units are part of the metric system, the conversion is based on powers of 10."
        ],
        formula: {
          main: "m = cm ÷ 100",
          alternate: "cm = m × 100"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'm',
          'm = cm ÷ 100',
          `${valueToUse} ÷ 100 = ${result || '0.3'}`,
          result || '0.3 m'
        ),
        realWorldExample: "A standard ruler is 30 centimeters, or 0.3 meters long. This shows how we commonly use both units depending on the scale of measurement.",
        funFacts: [
          "The prefix 'centi' means one hundredth (1/100)",
          "The meter was originally defined as one ten-millionth of the distance from the North Pole to the Equator",
          "Most countries use centimeters for everyday measurements"
        ]
      };
    }

    // Meters to Centimeters
    if (fromUnit === 'Meters' && toUnit === 'Centimeters') {
      return {
        introduction: [
          "Converting from meters to centimeters is a common metric conversion.",
          "This conversion is straightforward as it follows the metric system's base-10 pattern."
        ],
        formula: {
          main: "cm = m × 100",
          alternate: "m = cm ÷ 100"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'm',
          'cm',
          'cm = m × 100',
          `${valueToUse} × 100 = ${result || '3000'}`,
          result || '3000 cm'
        ),
        realWorldExample: "A person who is 1.75 meters tall is 175 centimeters tall. Height is commonly expressed in both units.",
        funFacts: [
          "The metric system was created to simplify measurements and calculations",
          "Moving down the metric scale (m to cm) means multiplying by powers of 10",
          "Centimeters are commonly used in clothing measurements worldwide"
        ]
      };
    }

    // Centimeters to Millimeters
    if (fromUnit === 'Centimeters' && toUnit === 'Millimeters') {
      return {
        introduction: [
          "Converting between centimeters and millimeters is one of the most basic metric conversions.",
          "These units are commonly used together in everyday measurements."
        ],
        formula: {
          main: "mm = cm × 10",
          alternate: "cm = mm ÷ 10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'mm',
          'mm = cm × 10',
          `${valueToUse} × 10 = ${result || '300'}`,
          result || '300 mm'
        ),
        realWorldExample: "A pencil is typically 1 centimeter (10 millimeters) in diameter. Rulers often show both units side by side.",
        funFacts: [
          "The prefix 'milli' means one thousandth (1/1000) of the base unit",
          "Millimeters are often used in precise measurements like rainfall",
          "Engineering drawings typically use millimeters for accuracy"
        ]
      };
    }

    // Micrometers to Nanometers
    if (fromUnit === 'Micrometers' && toUnit === 'Nanometers') {
      return {
        introduction: [
          "Converting between micrometers and nanometers is common in microscopic measurements.",
          "These units are essential in fields like nanotechnology and semiconductor manufacturing."
        ],
        formula: {
          main: "nm = μm × 1000",
          alternate: "μm = nm ÷ 1000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'μm',
          'nm',
          'nm = μm × 1000',
          `${valueToUse} × 1000 = ${result || '30000'}`,
          result || '30000 nm'
        ),
        realWorldExample: "A bacteria cell might be 1 micrometer (1000 nanometers) in size, while a virus is typically around 100 nanometers.",
        funFacts: [
          "A sheet of paper is about 100,000 nanometers thick",
          "The smallest things visible to the human eye are about 10,000 nanometers",
          "Nanometer-scale measurements are crucial in modern computer chip manufacturing"
        ]
      };
    }

    // Yards to Feet
    if (fromUnit === 'Yards' && toUnit === 'Feet') {
      return {
        introduction: [
          "Converting between yards and feet is a common imperial measurement conversion.",
          "The relationship is fixed: 1 yard equals 3 feet."
        ],
        fundamentalEquality: {
          statement: "1 yard = 3 feet",
          explanation: "Standard imperial conversion"
        },
        formula: {
          main: "ft = yd × 3",
          alternate: "yd = ft ÷ 3"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'yd',
          'ft',
          'ft = yd × 3',
          `${valueToUse} × 3 = ${result || '90'}`,
          result || '90 ft'
        )
      };
    }

    // Feet to Yards
    if (fromUnit === 'Feet' && toUnit === 'Yards') {
      return {
        introduction: [
          "Converting from feet to yards uses the basic imperial relationship.",
          "Three feet make one yard."
        ],
        formula: {
          main: "yd = ft ÷ 3",
          alternate: "ft = yd × 3"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ft',
          'yd',
          'yd = ft ÷ 3',
          `${valueToUse} ÷ 3 = ${result || '10'}`,
          result || '10 yd'
        )
      };
    }

    // Yards to Meters
    if (fromUnit === 'Yards' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting yards to meters connects imperial and metric systems.",
          "The conversion uses a precise factor."
        ],
        formula: {
          main: "m = yd × 0.9144",
          alternate: "yd = m ÷ 0.9144"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'yd',
          'm',
          'm = yd × 0.9144',
          `${valueToUse} × 0.9144 = ${result || '27.432'}`,
          result || '27.432 m'
        )
      };
    }

    // Meters to Yards
    if (fromUnit === 'Meters' && toUnit === 'Yards') {
      return {
        introduction: [
          "Converting meters to yards connects metric and imperial systems.",
          "The conversion uses the inverse of the yards-to-meters factor."
        ],
        formula: {
          main: "yd = m × 1.0936",
          alternate: "m = yd × 0.9144"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'm',
          'yd',
          'yd = m × 1.0936',
          `${valueToUse} × 1.0936 = ${result || '32.808'}`,
          result || '32.808 yd'
        )
      };
    }

    // Nautical Miles to Miles
    if (fromUnit === 'Nautical Miles' && toUnit === 'Miles') {
      return {
        introduction: [
          "Converting between nautical miles and statute miles.",
          "This conversion is common in navigation and aviation."
        ],
        formula: {
          main: "mi = nmi × 1.15078",
          alternate: "nmi = mi ÷ 1.15078"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'nmi',
          'mi',
          'mi = nmi × 1.15078',
          `${valueToUse} × 1.15078 = ${result || '34.5234'}`,
          result || '34.5234 mi'
        )
      };
    }

    // Miles to Nautical Miles
    if (fromUnit === 'Miles' && toUnit === 'Nautical Miles') {
      return {
        introduction: [
          "Converting from statute miles to nautical miles.",
          "Used frequently in maritime and aviation contexts."
        ],
        formula: {
          main: "nmi = mi ÷ 1.15078",
          alternate: "mi = nmi × 1.15078"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mi',
          'nmi',
          'nmi = mi ÷ 1.15078',
          `${valueToUse} ÷ 1.15078 = ${result || '26.0693'}`,
          result || '26.0693 nmi'
        )
      };
    }

    // Light Years to Astronomical Units
    if (fromUnit === 'Light Years' && toUnit === 'Astronomical Units') {
      return {
        introduction: [
          "Converting between light years and astronomical units.",
          "These units are used for measuring vast cosmic distances."
        ],
        formula: {
          main: "au = ly × 63241.1",
          alternate: "ly = au ÷ 63241.1"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'au',
          'au = ly × 63241.1',
          `${valueToUse} × 63241.1 = ${result || '1897233'}`,
          result || '1897233 au'
        )
      };
    }

    // Astronomical Units to Light Years
    if (fromUnit === 'Astronomical Units' && toUnit === 'Light Years') {
      return {
        introduction: [
          "Converting from astronomical units to light years.",
          "Common in astronomy for describing interstellar distances."
        ],
        formula: {
          main: "ly = au ÷ 63241.1",
          alternate: "au = ly × 63241.1"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'ly',
          'ly = au ÷ 63241.1',
          `${valueToUse} ÷ 63241.1 = ${result || '0.000474'}`,
          result || '0.000474 ly'
        )
      };
    }

    // Parsecs to Light Years
    if (fromUnit === 'Parsecs' && toUnit === 'Light Years') {
      return {
        introduction: [
          "Converting between parsecs and light years.",
          "Both units are used in astronomy for interstellar distances."
        ],
        formula: {
          main: "ly = pc × 3.26156",
          alternate: "pc = ly ÷ 3.26156"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'pc',
          'ly',
          'ly = pc × 3.26156',
          `${valueToUse} × 3.26156 = ${result || '97.8468'}`,
          result || '97.8468 ly'
        )
      };
    }

    // Light Years to Parsecs
    if (fromUnit === 'Light Years' && toUnit === 'Parsecs') {
      return {
        introduction: [
          "Converting from light years to parsecs.",
          "Common conversion in astronomical measurements."
        ],
        formula: {
          main: "pc = ly ÷ 3.26156",
          alternate: "ly = pc × 3.26156"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'pc',
          'pc = ly ÷ 3.26156',
          `${valueToUse} ÷ 3.26156 = ${result || '9.1982'}`,
          result || '9.1982 pc'
        )
      };
    }

    // Fathoms to Feet
    if (fromUnit === 'Fathoms' && toUnit === 'Feet') {
      return {
        introduction: [
          "Converting between fathoms and feet.",
          "Fathoms are traditionally used for water depth measurements."
        ],
        formula: {
          main: "ft = fm × 6",
          alternate: "fm = ft ÷ 6"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'fm',
          'ft',
          'ft = fm × 6',
          `${valueToUse} × 6 = ${result || '180'}`,
          result || '180 ft'
        )
      };
    }

    // Furlongs to Miles
    if (fromUnit === 'Furlongs' && toUnit === 'Miles') {
      return {
        introduction: [
          "Converting between furlongs and miles.",
          "Furlongs are still used in horse racing."
        ],
        formula: {
          main: "mi = fur ÷ 8",
          alternate: "fur = mi × 8"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'fur',
          'mi',
          'mi = fur ÷ 8',
          `${valueToUse} ÷ 8 = ${result || '3.75'}`,
          result || '3.75 mi'
        )
      };
    }

    // Chains to Yards
    if (fromUnit === 'Chains' && toUnit === 'Yards') {
      return {
        introduction: [
          "Converting between chains and yards.",
          "Chains were historically used in land surveying."
        ],
        formula: {
          main: "yd = ch × 22",
          alternate: "ch = yd ÷ 22"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ch',
          'yd',
          'yd = ch × 22',
          `${valueToUse} × 22 = ${result || '660'}`,
          result || '660 yd'
        )
      };
    }

    // Rods to Feet
    if (fromUnit === 'Rods' && toUnit === 'Feet') {
      return {
        introduction: [
          "Converting between rods and feet.",
          "Rods were used in land surveying."
        ],
        formula: {
          main: "ft = rd × 16.5",
          alternate: "rd = ft ÷ 16.5"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'rd',
          'ft',
          'ft = rd × 16.5',
          `${valueToUse} × 16.5 = ${result || '495'}`,
          result || '495 ft'
        )
      };
    }

    // Links to Inches
    if (fromUnit === 'Links' && toUnit === 'Inches') {
      return {
        introduction: [
          "Converting between links and inches.",
          "Links were used in surveying chains."
        ],
        formula: {
          main: "in = li × 7.92",
          alternate: "li = in ÷ 7.92"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'li',
          'in',
          'in = li × 7.92',
          `${valueToUse} × 7.92 = ${result || '237.6'}`,
          result || '237.6 in'
        )
      };
    }

    // Feet to Rods
    if (fromUnit === 'Feet' && toUnit === 'Rods') {
      return {
        introduction: [
          "Converting from feet to rods.",
          "Used in historical land surveying."
        ],
        formula: {
          main: "rd = ft ÷ 16.5",
          alternate: "ft = rd × 16.5"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ft',
          'rd',
          'rd = ft ÷ 16.5',
          `${valueToUse} ÷ 16.5 = ${result || '1.81818'}`,
          result || '1.81818 rd'
        )
      };
    }

    // Inches to Links
    if (fromUnit === 'Inches' && toUnit === 'Links') {
      return {
        introduction: [
          "Converting from inches to links.",
          "Links were part of Gunter's surveying chain."
        ],
        formula: {
          main: "li = in ÷ 7.92",
          alternate: "in = li × 7.92"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'in',
          'li',
          'li = in ÷ 7.92',
          `${valueToUse} ÷ 7.92 = ${result || '3.78788'}`,
          result || '3.78788 li'
        )
      };
    }

    // Chains to Rods
    if (fromUnit === 'Chains' && toUnit === 'Rods') {
      return {
        introduction: [
          "Converting between chains and rods.",
          "Both units were used in land surveying."
        ],
        formula: {
          main: "rd = ch × 4",
          alternate: "ch = rd ÷ 4"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ch',
          'rd',
          'rd = ch × 4',
          `${valueToUse} × 4 = ${result || '120'}`,
          result || '120 rd'
        )
      };
    }

    // Links to Chains
    if (fromUnit === 'Links' && toUnit === 'Chains') {
      return {
        introduction: [
          "Converting from links to chains.",
          "100 links make up one chain."
        ],
        formula: {
          main: "ch = li ÷ 100",
          alternate: "li = ch × 100"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'li',
          'ch',
          'ch = li ÷ 100',
          `${valueToUse} ÷ 100 = ${result || '0.3'}`,
          result || '0.3 ch'
        )
      };
    }

    // Angstroms to Nanometers
    if (fromUnit === 'Angstroms' && toUnit === 'Nanometers') {
      return {
        introduction: [
          "Converting between angstroms and nanometers.",
          "Both units are used for atomic and molecular measurements."
        ],
        formula: {
          main: "nm = Å ÷ 10",
          alternate: "Å = nm × 10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'nm',
          'nm = Å ÷ 10',
          `${valueToUse} ÷ 10 = ${result || '3'}`,
          result || '3 nm'
        )
      };
    }

    // Nanometers to Angstroms
    if (fromUnit === 'Nanometers' && toUnit === 'Angstroms') {
      return {
        introduction: [
          "Converting from nanometers to angstroms.",
          "Common in spectroscopy and crystallography."
        ],
        formula: {
          main: "Å = nm × 10",
          alternate: "nm = Å ÷ 10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'nm',
          'Å',
          'Å = nm × 10',
          `${valueToUse} × 10 = ${result || '300'}`,
          result || '300 Å'
        )
      };
    }

    // Mils to Inches
    if (fromUnit === 'Mils' && toUnit === 'Inches') {
      return {
        introduction: [
          "Converting between mils and inches.",
          "Mils are commonly used in engineering and manufacturing."
        ],
        formula: {
          main: "in = mil ÷ 1000",
          alternate: "mil = in × 1000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mil',
          'in',
          'in = mil ÷ 1000',
          `${valueToUse} ÷ 1000 = ${result || '0.03'}`,
          result || '0.03 in'
        )
      };
    }

    // Mils to Millimeters
    if (fromUnit === 'Mils' && toUnit === 'Millimeters') {
      return {
        introduction: [
          "Converting between mils and millimeters.",
          "Used in technical specifications."
        ],
        formula: {
          main: "mm = mil × 0.0254",
          alternate: "mil = mm ÷ 0.0254"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mil',
          'mm',
          'mm = mil × 0.0254',
          `${valueToUse} × 0.0254 = ${result || '0.762'}`,
          result || '0.762 mm'
        )
      };
    }

    // Miles to Nautical Miles
    if (fromUnit === 'Miles' && toUnit === 'Nautical Miles') {
      return {
        introduction: [
          "Converting between statute miles and nautical miles.",
          "Used in navigation and aviation."
        ],
        formula: {
          main: "nmi = mi × 0.868976",
          alternate: "mi = nmi × 1.15078"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mi',
          'nmi',
          'nmi = mi × 0.868976',
          `${valueToUse} × 0.868976 = ${result || '26.06928'}`,
          result || '26.06928 nmi'
        )
      };
    }

    // Parsecs to Astronomical Units
    if (fromUnit === 'Parsecs' && toUnit === 'Astronomical Units') {
      return {
        introduction: [
          "Converting between parsecs and astronomical units.",
          "Used for measuring vast cosmic distances."
        ],
        formula: {
          main: "au = pc × 206265",
          alternate: "pc = au ÷ 206265"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'pc',
          'au',
          'au = pc × 206265',
          `${valueToUse} × 206265 = ${result || '6187950'}`,
          result || '6187950 au'
        )
      };
    }

    // Astronomical Units to Light Years
    if (fromUnit === 'Astronomical Units' && toUnit === 'Light Years') {
      return {
        introduction: [
          "Converting between astronomical units and light years.",
          "Common in astronomy and space science."
        ],
        formula: {
          main: "ly = au ÷ 63241.1",
          alternate: "au = ly × 63241.1"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'ly',
          'ly = au ÷ 63241.1',
          `${valueToUse} ÷ 63241.1 = ${result || '0.000474'}`,
          result || '0.000474 ly'
        )
      };
    }

    // Kilometers to Nautical Miles
    if (fromUnit === 'Kilometers' && toUnit === 'Nautical Miles') {
      return {
        introduction: [
          "Converting between kilometers and nautical miles.",
          "Important for maritime navigation and aviation."
        ],
        formula: {
          main: "nmi = km × 0.539957",
          alternate: "km = nmi × 1.852"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'km',
          'nmi',
          'nmi = km × 0.539957',
          `${valueToUse} × 0.539957 = ${result || '16.19871'}`,
          result || '16.19871 nmi'
        )
      };
    }
    // Kilometers to Miles
    if (fromUnit === 'Kilometers' && toUnit === 'Miles') {
      return {
        introduction: [
          "Converting between kilometers and miles.",
          "Common when traveling between countries using different systems."
        ],
        formula: {
          main: "mi = km ÷ 1.60934",
          alternate: "km = mi × 1.60934"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'km',
          'mi',
          'mi = km ÷ 1.60934',
          `${valueToUse} ÷ 1.60934 = ${result || '18.64114'}`,
          result || '18.64114 mi'
        )
      };
    }

    // Miles to Kilometers
    if (fromUnit === 'Miles' && toUnit === 'Kilometers') {
      return {
        introduction: [
          "Converting from miles to kilometers.",
          "Used when converting between imperial and metric distances."
        ],
        formula: {
          main: "km = mi × 1.60934",
          alternate: "mi = km ÷ 1.60934"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mi',
          'km',
          'km = mi × 1.60934',
          `${valueToUse} × 1.60934 = ${result || '48.28020'}`,
          result || '48.28020 km'
        )
      };
    }

    // Centimeters to Inches
    if (fromUnit === 'Centimeters' && toUnit === 'Inches') {
      return {
        introduction: [
          "Converting between centimeters and inches.",
          "Common in everyday measurements and construction."
        ],
        formula: {
          main: "in = cm ÷ 2.54",
          alternate: "cm = in × 2.54"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'in',
          'in = cm ÷ 2.54',
          `${valueToUse} ÷ 2.54 = ${result || '11.81102'}`,
          result || '11.81102 in'
        )
      };
    }

    // Inches to Centimeters
    if (fromUnit === 'Inches' && toUnit === 'Centimeters') {
      return {
        introduction: [
          "Converting from inches to centimeters.",
          "Used in international manufacturing and design."
        ],
        formula: {
          main: "cm = in × 2.54",
          alternate: "in = cm ÷ 2.54"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'in',
          'cm',
          'cm = in × 2.54',
          `${valueToUse} × 2.54 = ${result || '76.2'}`,
          result || '76.2 cm'
        )
      };
    }

    // Millimeters to Inches
    if (fromUnit === 'Millimeters' && toUnit === 'Inches') {
      return {
        introduction: [
          "Converting between millimeters and inches.",
          "Common in technical drawings and engineering."
        ],
        formula: {
          main: "in = mm ÷ 25.4",
          alternate: "mm = in × 25.4"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mm',
          'in',
          'in = mm ÷ 25.4',
          `${valueToUse} ÷ 25.4 = ${result || '1.18110'}`,
          result || '1.18110 in'
        )
      };
    }

    // Meters to Yards
    if (fromUnit === 'Meters' && toUnit === 'Yards') {
      return {
        introduction: [
          "Converting between meters and yards.",
          "Used in sports and construction measurements."
        ],
        formula: {
          main: "yd = m × 1.09361",
          alternate: "m = yd × 0.9144"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'm',
          'yd',
          'yd = m × 1.09361',
          `${valueToUse} × 1.09361 = ${result || '32.80830'}`,
          result || '32.80830 yd'
        )
      };
    }

    // Furlongs to Yards
    if (fromUnit === 'Furlongs' && toUnit === 'Yards') {
      return {
        introduction: [
          "Converting between furlongs and yards.",
          "Used in horse racing and historical measurements."
        ],
        formula: {
          main: "yd = fur × 220",
          alternate: "fur = yd ÷ 220"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'fur',
          'yd',
          'yd = fur × 220',
          `${valueToUse} × 220 = ${result || '6600'}`,
          result || '6600 yd'
        )
      };
    }

    // Fathoms to Yards
    if (fromUnit === 'Fathoms' && toUnit === 'Yards') {
      return {
        introduction: [
          "Converting between fathoms and yards.",
          "Used in maritime depth measurements."
        ],
        formula: {
          main: "yd = ftm × 2",
          alternate: "ftm = yd ÷ 2"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ftm',
          'yd',
          'yd = ftm × 2',
          `${valueToUse} × 2 = ${result || '60'}`,
          result || '60 yd'
        )
      };
    }

    // Yards to Meters
    if (fromUnit === 'Yards' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting from yards to meters.",
          "Common in international sports and construction."
        ],
        formula: {
          main: "m = yd × 0.9144",
          alternate: "yd = m × 1.09361"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'yd',
          'm',
          'm = yd × 0.9144',
          `${valueToUse} × 0.9144 = ${result || '27.432'}`,
          result || '27.432 m'
        )
      };
    }

    // Inches to Millimeters
    if (fromUnit === 'Inches' && toUnit === 'Millimeters') {
      return {
        introduction: [
          "Converting from inches to millimeters.",
          "Used in engineering and technical specifications."
        ],
        formula: {
          main: "mm = in × 25.4",
          alternate: "in = mm ÷ 25.4"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'in',
          'mm',
          'mm = in × 25.4',
          `${valueToUse} × 25.4 = ${result || '762'}`,
          result || '762 mm'
        )
      };
    }

    // Yards to Fathoms
    if (fromUnit === 'Yards' && toUnit === 'Fathoms') {
      return {
        introduction: [
          "Converting from yards to fathoms.",
          "Used in maritime measurements."
        ],
        formula: {
          main: "ftm = yd ÷ 2",
          alternate: "yd = ftm × 2"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'yd',
          'ftm',
          'ftm = yd ÷ 2',
          `${valueToUse} ÷ 2 = ${result || '15'}`,
          result || '15 ftm'
        )
      };
    }

    // Yards to Furlongs
    if (fromUnit === 'Yards' && toUnit === 'Furlongs') {
      return {
        introduction: [
          "Converting from yards to furlongs.",
          "Used in horse racing and land measurement."
        ],
        formula: {
          main: "fur = yd ÷ 220",
          alternate: "yd = fur × 220"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'yd',
          'fur',
          'fur = yd ÷ 220',
          `${valueToUse} ÷ 220 = ${result || '0.136364'}`,
          result || '0.136364 fur'
        )
      };
    }

    // Chains to Furlongs
    if (fromUnit === 'Chains' && toUnit === 'Furlongs') {
      return {
        introduction: [
          "Converting between chains and furlongs.",
          "Historical units used in land surveying."
        ],
        formula: {
          main: "fur = ch ÷ 10",
          alternate: "ch = fur × 10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ch',
          'fur',
          'fur = ch ÷ 10',
          `${valueToUse} ÷ 10 = ${result || '3'}`,
          result || '3 fur'
        )
      };
    }

    // Rods to Chains
    if (fromUnit === 'Rods' && toUnit === 'Chains') {
      return {
        introduction: [
          "Converting between rods and chains.",
          "Used in historical land measurements."
        ],
        formula: {
          main: "ch = rd ÷ 4",
          alternate: "rd = ch × 4"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'rd',
          'ch',
          'ch = rd ÷ 4',
          `${valueToUse} ÷ 4 = ${result || '7.5'}`,
          result || '7.5 ch'
        )
      };
    }

    // Links to Rods
    if (fromUnit === 'Links' && toUnit === 'Rods') {
      return {
        introduction: [
          "Converting between links and rods.",
          "Historical surveying units."
        ],
        formula: {
          main: "rd = li ÷ 25",
          alternate: "li = rd × 25"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'li',
          'rd',
          'rd = li ÷ 25',
          `${valueToUse} ÷ 25 = ${result || '1.2'}`,
          result || '1.2 rd'
        )
      };
    }

    // Rods to Links
    if (fromUnit === 'Rods' && toUnit === 'Links') {
      return {
        introduction: [
          "Converting from rods to links.",
          "Used in historical land surveying."
        ],
        formula: {
          main: "li = rd × 25",
          alternate: "rd = li ÷ 25"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'rd',
          'li',
          'li = rd × 25',
          `${valueToUse} × 25 = ${result || '750'}`,
          result || '750 li'
        )
      };
    }

    // Furlongs to Chains
    if (fromUnit === 'Furlongs' && toUnit === 'Chains') {
      return {
        introduction: [
          "Converting from furlongs to chains.",
          "Historical land measurement units."
        ],
        formula: {
          main: "ch = fur × 10",
          alternate: "fur = ch ÷ 10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'fur',
          'ch',
          'ch = fur × 10',
          `${valueToUse} × 10 = ${result || '300'}`,
          result || '300 ch'
        )
      };
    }

    // Chains to Rods
    if (fromUnit === 'Chains' && toUnit === 'Rods') {
      return {
        introduction: [
          "Converting from chains to rods.",
          "Used in historical surveying."
        ],
        formula: {
          main: "rd = ch × 4",
          alternate: "ch = rd ÷ 4"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ch',
          'rd',
          'rd = ch × 4',
          `${valueToUse} × 4 = ${result || '120'}`,
          result || '120 rd'
        )
      };
    }

    // Furlongs to Rods
    if (fromUnit === 'Furlongs' && toUnit === 'Rods') {
      return {
        introduction: [
          "Converting between furlongs and rods.",
          "Historical distance measurements."
        ],
        formula: {
          main: "rd = fur × 40",
          alternate: "fur = rd ÷ 40"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'fur',
          'rd',
          'rd = fur × 40',
          `${valueToUse} × 40 = ${result || '1200'}`,
          result || '1200 rd'
        )
      };
    }

    // Rods to Furlongs
    if (fromUnit === 'Rods' && toUnit === 'Furlongs') {
      return {
        introduction: [
          "Converting from rods to furlongs.",
          "Historical surveying units."
        ],
        formula: {
          main: "fur = rd ÷ 40",
          alternate: "rd = fur × 40"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'rd',
          'fur',
          'fur = rd ÷ 40',
          `${valueToUse} ÷ 40 = ${result || '0.75'}`,
          result || '0.75 fur'
        )
      };
    }

    // Nanometers to Micrometers
    if (fromUnit === 'Nanometers' && toUnit === 'Micrometers') {
      return {
        introduction: [
          "Converting between nanometers and micrometers.",
          "Used in microscopic measurements."
        ],
        formula: {
          main: "μm = nm ÷ 1000",
          alternate: "nm = μm × 1000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'nm',
          'μm',
          'μm = nm ÷ 1000',
          `${valueToUse} ÷ 1000 = ${result || '0.03'}`,
          result || '0.03 μm'
        )
      };
    }

    // Micrometers to Millimeters
    if (fromUnit === 'Micrometers' && toUnit === 'Millimeters') {
      return {
        introduction: [
          "Converting between micrometers and millimeters.",
          "Common in precision engineering."
        ],
        formula: {
          main: "mm = μm ÷ 1000",
          alternate: "μm = mm × 1000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'μm',
          'mm',
          'mm = μm ÷ 1000',
          `${valueToUse} ÷ 1000 = ${result || '0.03'}`,
          result || '0.03 mm'
        )
      };
    }

    // Nautical Miles to Kilometers
    if (fromUnit === 'Nautical Miles' && toUnit === 'Kilometers') {
      return {
        introduction: [
          "Converting from nautical miles to kilometers.",
          "Used in navigation and aviation."
        ],
        formula: {
          main: "km = nmi × 1.852",
          alternate: "nmi = km ÷ 1.852"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'nmi',
          'km',
          'km = nmi × 1.852',
          `${valueToUse} × 1.852 = ${result || '55.56'}`,
          result || '55.56 km'
        )
      };
    }

    // Microns to Mils
    if (fromUnit === 'Microns' && toUnit === 'Mils') {
      return {
        introduction: [
          "Converting between microns and mils.",
          "Used in engineering and manufacturing."
        ],
        formula: {
          main: "mil = μm ÷ 25.4",
          alternate: "μm = mil × 25.4"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'μm',
          'mil',
          'mil = μm ÷ 25.4',
          `${valueToUse} ÷ 25.4 = ${result || '1.18110'}`,
          result || '1.18110 mil'
        )
      };
    }

    // Micrometers to Nanometers
    if (fromUnit === 'Micrometers' && toUnit === 'Nanometers') {
      return {
        introduction: [
          "Converting from micrometers to nanometers.",
          "Used in microscopic measurements."
        ],
        formula: {
          main: "nm = μm × 1000",
          alternate: "μm = nm ÷ 1000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'μm',
          'nm',
          'nm = μm × 1000',
          `${valueToUse} × 1000 = ${result || '30000'}`,
          result || '30000 nm'
        )
      };
    }

    // Kilometers to Nautical Miles
    if (fromUnit === 'Kilometers' && toUnit === 'Nautical Miles') {
      return {
        introduction: [
          "Converting from kilometers to nautical miles.",
          "Used in navigation and aviation."
        ],
        formula: {
          main: "nmi = km ÷ 1.852",
          alternate: "km = nmi × 1.852"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'km',
          'nmi',
          'nmi = km ÷ 1.852',
          `${valueToUse} ÷ 1.852 = ${result || '16.19871'}`,
          result || '16.19871 nmi'
        )
      };
    }

    // Parsecs to Light Years
    if (fromUnit === 'Parsecs' && toUnit === 'Light Years') {
      return {
        introduction: [
          "Converting from parsecs to light years.",
          "Used in astronomical measurements."
        ],
        formula: {
          main: "ly = pc × 3.26156",
          alternate: "pc = ly ÷ 3.26156"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'pc',
          'ly',
          'ly = pc × 3.26156',
          `${valueToUse} × 3.26156 = ${result || '97.8468'}`,
          result || '97.8468 ly'
        )
      };
    }

    // Light Years to Astronomical Units
    if (fromUnit === 'Light Years' && toUnit === 'Astronomical Units') {
      return {
        introduction: [
          "Converting from light years to astronomical units.",
          "Used in space science."
        ],
        formula: {
          main: "au = ly × 63241.1",
          alternate: "ly = au ÷ 63241.1"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'au',
          'au = ly × 63241.1',
          `${valueToUse} × 63241.1 = ${result || '1897233'}`,
          result || '1897233 au'
        )
      };
    }

    // Parsecs to Astronomical Units
    if (fromUnit === 'Parsecs' && toUnit === 'Astronomical Units') {
      return {
        introduction: [
          "Converting from parsecs to astronomical units.",
          "Used in astronomical measurements."
        ],
        formula: {
          main: "au = pc × 206265",
          alternate: "pc = au ÷ 206265"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'pc',
          'au',
          'au = pc × 206265',
          `${valueToUse} × 206265 = ${result || '6187950'}`,
          result || '6187950 au'
        )
      };
    }

    // Astronomical Units to Parsecs
    if (fromUnit === 'Astronomical Units' && toUnit === 'Parsecs') {
      return {
        introduction: [
          "Converting from astronomical units to parsecs.",
          "Used in space science."
        ],
        formula: {
          main: "pc = au ÷ 206265",
          alternate: "au = pc × 206265"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'pc',
          'pc = au ÷ 206265',
          `${valueToUse} ÷ 206265 = ${result || '0.000145'}`,
          result || '0.000145 pc'
        )
      };
    }

    // Millimeters to Micrometers
    if (fromUnit === 'Millimeters' && toUnit === 'Micrometers') {
      return {
        introduction: [
          "Converting from millimeters to micrometers.",
          "Used in precision measurements."
        ],
        formula: {
          main: "μm = mm × 1000",
          alternate: "mm = μm ÷ 1000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mm',
          'μm',
          'μm = mm × 1000',
          `${valueToUse} × 1000 = ${result || '30000'}`,
          result || '30000 μm'
        )
      };
    }

    // Mils to Microns
    if (fromUnit === 'Mils' && toUnit === 'Microns') {
      return {
        introduction: [
          "Converting from mils to microns.",
          "Used in engineering specifications."
        ],
        formula: {
          main: "μm = mil × 25.4",
          alternate: "mil = μm ÷ 25.4"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mil',
          'μm',
          'μm = mil × 25.4',
          `${valueToUse} × 25.4 = ${result || '762'}`,
          result || '762 μm'
        )
      };
    }

    // Nanometers to Angstroms
    if (fromUnit === 'Nanometers' && toUnit === 'Angstroms') {
      return {
        introduction: [
          "Converting between nanometers and angstroms.",
          "Used in atomic and molecular measurements."
        ],
        formula: {
          main: "Å = nm × 10",
          alternate: "nm = Å ÷ 10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'nm',
          'Å',
          'Å = nm × 10',
          `${valueToUse} × 10 = ${result || '300'}`,
          result || '300 Å'
        )
      };
    }

    // Centimeters to Millimeters
    if (fromUnit === 'Centimeters' && toUnit === 'Millimeters') {
      return {
        introduction: [
          "Converting between centimeters and millimeters.",
          "Basic metric system conversion."
        ],
        formula: {
          main: "mm = cm × 10",
          alternate: "cm = mm ÷ 10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'mm',
          'mm = cm × 10',
          `${valueToUse} × 10 = ${result || '300'}`,
          result || '300 mm'
        )
      };
    }

    // Miles to Feet
    if (fromUnit === 'Miles' && toUnit === 'Feet') {
      return {
        introduction: [
          "Converting between miles and feet.",
          "Common in US measurements."
        ],
        formula: {
          main: "ft = mi × 5280",
          alternate: "mi = ft ÷ 5280"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mi',
          'ft',
          'ft = mi × 5280',
          `${valueToUse} × 5280 = ${result || '158400'}`,
          result || '158400 ft'
        )
      };
    }

    // Miles to Yards
    if (fromUnit === 'Miles' && toUnit === 'Yards') {
      return {
        introduction: [
          "Converting between miles and yards.",
          "Used in distance measurements."
        ],
        formula: {
          main: "yd = mi × 1760",
          alternate: "mi = yd ÷ 1760"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mi',
          'yd',
          'yd = mi × 1760',
          `${valueToUse} × 1760 = ${result || '52800'}`,
          result || '52800 yd'
        )
      };
    }

    // Meters to Centimeters
    if (fromUnit === 'Meters' && toUnit === 'Centimeters') {
      return {
        introduction: [
          "Converting between meters and centimeters.",
          "Basic metric system conversion using powers of 10."
        ],
        formula: {
          main: "cm = m × 100",
          alternate: "m = cm ÷ 100"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'm',
          'cm',
          'cm = m × 100',
          `${valueToUse} × 100 = ${result || '3000'}`,
          result || '3000 cm'
        )
      };
    }

    // Millimeters to Nanometers
    if (fromUnit === 'Millimeters' && toUnit === 'Nanometers') {
      return {
        introduction: [
          "Converting between millimeters and nanometers.",
          "Used in precision measurements and microscopy."
        ],
        formula: {
          main: "nm = mm × 1000000",
          alternate: "mm = nm ÷ 1000000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mm',
          'nm',
          'nm = mm × 1000000',
          `${valueToUse} × 1000000 = ${result || '30000000'}`,
          result || '30000000 nm'
        )
      };
    }

    // Centimeters to Meters
    if (fromUnit === 'Centimeters' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting between centimeters and meters.",
          "Common metric conversion in everyday measurements."
        ],
        formula: {
          main: "m = cm ÷ 100",
          alternate: "cm = m × 100"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'm',
          'm = cm ÷ 100',
          `${valueToUse} ÷ 100 = ${result || '0.3'}`,
          result || '0.3 m'
        )
      };
    }

    // Micrometers to Millimeters
    if (fromUnit === 'Micrometers' && toUnit === 'Millimeters') {
      return {
        introduction: [
          "Converting between micrometers and millimeters.",
          "Used in precision engineering and manufacturing."
        ],
        formula: {
          main: "mm = μm ÷ 1000",
          alternate: "μm = mm × 1000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'μm',
          'mm',
          'mm = μm ÷ 1000',
          `${valueToUse} ÷ 1000 = ${result || '0.03'}`,
          result || '0.03 mm'
        )
      };
    }

    // Meters to Inches
    if (fromUnit === 'Meters' && toUnit === 'Inches') {
      return {
        introduction: [
          "Converting from meters to inches.",
          "Common in international manufacturing and design."
        ],
        formula: {
          main: "in = m × 39.3701",
          alternate: "m = in ÷ 39.3701"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'm',
          'in',
          'in = m × 39.3701',
          `${valueToUse} × 39.3701 = ${result || '1181.103'}`,
          result || '1181.103 in'
        )
      };
    }

    // Feet to Yards
    if (fromUnit === 'Feet' && toUnit === 'Yards') {
      return {
        introduction: [
          "Converting between feet and yards.",
          "Common in sports and construction measurements."
        ],
        formula: {
          main: "yd = ft ÷ 3",
          alternate: "ft = yd × 3"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ft',
          'yd',
          'yd = ft ÷ 3',
          `${valueToUse} ÷ 3 = ${result || '10'}`,
          result || '10 yd'
        )
      };
    }

    // Angstroms to Micrometers
    if (fromUnit === 'Angstroms' && toUnit === 'Micrometers') {
      return {
        introduction: [
          "Converting from angstroms to micrometers.",
          "Used in microscopic and atomic measurements."
        ],
        formula: {
          main: "μm = Å ÷ 10000",
          alternate: "Å = μm × 10000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'μm',
          'μm = Å ÷ 10000',
          `${valueToUse} ÷ 10000 = ${result || '0.003'}`,
          result || '0.003 μm'
        )
      };
    }

    // Microns to Nanometers
    if (fromUnit === 'Microns' && toUnit === 'Nanometers') {
      return {
        introduction: [
          "Converting between microns and nanometers.",
          "Used in scientific and engineering measurements."
        ],
        formula: {
          main: "nm = μ × 1000",
          alternate: "μ = nm ÷ 1000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'μ',
          'nm',
          'nm = μ × 1000',
          `${valueToUse} × 1000 = ${result || '30000'}`,
          result || '30000 nm'
        )
      };
    }

    // Yards to Meters
    if (fromUnit === 'Yards' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting from yards to meters.",
          "Common in sports and international measurements."
        ],
        formula: {
          main: "m = yd × 0.9144",
          alternate: "yd = m ÷ 0.9144"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'yd',
          'm',
          'm = yd × 0.9144',
          `${valueToUse} × 0.9144 = ${result || '27.432'}`,
          result || '27.432 m'
        )
      };
    }

    // Inches to Centimeters
    if (fromUnit === 'Inches' && toUnit === 'Centimeters') {
      return {
        introduction: [
          "Converting from inches to centimeters.",
          "Common in everyday measurements and manufacturing."
        ],
        formula: {
          main: "cm = in × 2.54",
          alternate: "in = cm ÷ 2.54"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'in',
          'cm',
          'cm = in × 2.54',
          `${valueToUse} × 2.54 = ${result || '76.2'}`,
          result || '76.2 cm'
        )
      };
    }

    // Feet to Millimeters
    if (fromUnit === 'Feet' && toUnit === 'Millimeters') {
      return {
        introduction: [
          "Converting from feet to millimeters.",
          "Used in precision engineering and construction."
        ],
        formula: {
          main: "mm = ft × 304.8",
          alternate: "ft = mm ÷ 304.8"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ft',
          'mm',
          'mm = ft × 304.8',
          `${valueToUse} × 304.8 = ${result || '9144'}`,
          result || '9144 mm'
        )
      };
    }

    // Yards to Kilometers
    if (fromUnit === 'Yards' && toUnit === 'Kilometers') {
      return {
        introduction: [
          "Converting from yards to kilometers.",
          "Used in distance measurements and sports."
        ],
        formula: {
          main: "km = yd × 0.0009144",
          alternate: "yd = km ÷ 0.0009144"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'yd',
          'km',
          'km = yd × 0.0009144',
          `${valueToUse} × 0.0009144 = ${result || '0.027432'}`,
          result || '0.027432 km'
        )
      };
    }

    // Nanometers to Microns
    if (fromUnit === 'Nanometers' && toUnit === 'Microns') {
      return {
        introduction: [
          "Converting from nanometers to microns.",
          "Used in microscopy and scientific measurements."
        ],
        formula: {
          main: "μ = nm ÷ 1000",
          alternate: "nm = μ × 1000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'nm',
          'μ',
          'μ = nm ÷ 1000',
          `${valueToUse} ÷ 1000 = ${result || '0.03'}`,
          result || '0.03 μ'
        )
      };
    }

    // Astronomical Units to Parsecs
    if (fromUnit === 'Astronomical Units' && toUnit === 'Parsecs') {
      return {
        introduction: [
          "Converting from astronomical units to parsecs.",
          "Used in astronomical measurements."
        ],
        formula: {
          main: "pc = au ÷ 206265",
          alternate: "au = pc × 206265"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'pc',
          'pc = au ÷ 206265',
          `${valueToUse} ÷ 206265 = ${result || '0.000145'}`,
          result || '0.000145 pc'
        )
      };
    }

    // Millimeters to Mils
    if (fromUnit === 'Millimeters' && toUnit === 'Mils') {
      return {
        introduction: [
          "Converting from millimeters to mils.",
          "Used in engineering and manufacturing."
        ],
        formula: {
          main: "mil = mm × 39.3701",
          alternate: "mm = mil ÷ 39.3701"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mm',
          'mil',
          'mil = mm × 39.3701',
          `${valueToUse} × 39.3701 = ${result || '1181.103'}`,
          result || '1181.103 mil'
        )
      };
    }

    // Microns to Angstroms
    if (fromUnit === 'Microns' && toUnit === 'Angstroms') {
      return {
        introduction: [
          "Converting from microns to angstroms.",
          "Used in spectroscopy and crystallography."
        ],
        formula: {
          main: "Å = μ × 10000",
          alternate: "μ = Å ÷ 10000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'μ',
          'Å',
          'Å = μ × 10000',
          `${valueToUse} × 10000 = ${result || '300000'}`,
          result || '300000 Å'
        )
      };
    }

    // Angstroms to Nanometers
    if (fromUnit === 'Angstroms' && toUnit === 'Nanometers') {
      return {
        introduction: [
          "Converting from angstroms to nanometers.",
          "Used in atomic and molecular measurements."
        ],
        formula: {
          main: "nm = Å ÷ 10",
          alternate: "Å = nm × 10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'nm',
          'nm = Å ÷ 10',
          `${valueToUse} ÷ 10 = ${result || '3'}`,
          result || '3 nm'
        )
      };
    }

    // Mils to Microns
    if (fromUnit === 'Mils' && toUnit === 'Microns') {
      return {
        introduction: [
          "Converting from mils to microns.",
          "Used in precision engineering."
        ],
        formula: {
          main: "μ = mil × 25.4",
          alternate: "mil = μ ÷ 25.4"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mil',
          'μ',
          'μ = mil × 25.4',
          `${valueToUse} × 25.4 = ${result || '762'}`,
          result || '762 μ'
        )
      };
    }

    // Nautical Miles to Miles
    if (fromUnit === 'Nautical Miles' && toUnit === 'Miles') {
      return {
        introduction: [
          "Converting from nautical miles to statute miles.",
          "Used in navigation and aviation."
        ],
        formula: {
          main: "mi = nmi × 1.15078",
          alternate: "nmi = mi ÷ 1.15078"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'nmi',
          'mi',
          'mi = nmi × 1.15078',
          `${valueToUse} × 1.15078 = ${result || '34.5234'}`,
          result || '34.5234 mi'
        )
      };
    }

    // Fathoms to Meters
    if (fromUnit === 'Fathoms' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting from fathoms to meters.",
          "Used in maritime depth measurements."
        ],
        formula: {
          main: "m = ftm × 1.8288",
          alternate: "ftm = m ÷ 1.8288"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ftm',
          'm',
          'm = ftm × 1.8288',
          `${valueToUse} × 1.8288 = ${result || '54.864'}`,
          result || '54.864 m'
        )
      };
    }

    // Meters to Fathoms
    if (fromUnit === 'Meters' && toUnit === 'Fathoms') {
      return {
        introduction: [
          "Converting from meters to fathoms.",
          "Used in maritime depth measurements."
        ],
        formula: {
          main: "ftm = m ÷ 1.8288",
          alternate: "m = ftm × 1.8288"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'm',
          'ftm',
          'ftm = m ÷ 1.8288',
          `${valueToUse} ÷ 1.8288 = ${result || '16.4042'}`,
          result || '16.4042 ftm'
        )
      };
    }

    // Miles to Nautical Miles
    if (fromUnit === 'Miles' && toUnit === 'Nautical Miles') {
      return {
        introduction: [
          "Converting from statute miles to nautical miles.",
          "Used in navigation and aviation."
        ],
        formula: {
          main: "nmi = mi ÷ 1.15078",
          alternate: "mi = nmi × 1.15078"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mi',
          'nmi',
          'nmi = mi ÷ 1.15078',
          `${valueToUse} ÷ 1.15078 = ${result || '26.0694'}`,
          result || '26.0694 nmi'
        )
      };
    }

    // Microns to Mils
    if (fromUnit === 'Microns' && toUnit === 'Mils') {
      return {
        introduction: [
          "Converting from microns to mils.",
          "Used in precision engineering and manufacturing."
        ],
        formula: {
          main: "mil = μ ÷ 25.4",
          alternate: "μ = mil × 25.4"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'μ',
          'mil',
          'mil = μ ÷ 25.4',
          `${valueToUse} ÷ 25.4 = ${result || '1.1811'}`,
          result || '1.1811 mil'
        )
      };
    }

    // Angstroms to Microns
    if (fromUnit === 'Angstroms' && toUnit === 'Microns') {
      return {
        introduction: [
          "Converting from angstroms to microns.",
          "Used in microscopy and crystallography."
        ],
        formula: {
          main: "μ = Å ÷ 10000",
          alternate: "Å = μ × 10000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'μ',
          'μ = Å ÷ 10000',
          `${valueToUse} ÷ 10000 = ${result || '0.003'}`,
          result || '0.003 μ'
        )
      };
    }

    // Fathoms to Yards
    if (fromUnit === 'Fathoms' && toUnit === 'Yards') {
      return {
        introduction: [
          "Converting from fathoms to yards.",
          "Used in maritime and historical measurements."
        ],
        formula: {
          main: "yd = ftm × 2",
          alternate: "ftm = yd ÷ 2"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ftm',
          'yd',
          'yd = ftm × 2',
          `${valueToUse} × 2 = ${result || '60'}`,
          result || '60 yd'
        )
      };
    }

    // Light Years to Parsecs
    if (fromUnit === 'Light Years' && toUnit === 'Parsecs') {
      return {
        introduction: [
          "Converting from light years to parsecs.",
          "Used in astronomical measurements."
        ],
        formula: {
          main: "pc = ly ÷ 3.26156",
          alternate: "ly = pc × 3.26156"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'pc',
          'pc = ly ÷ 3.26156',
          `${valueToUse} ÷ 3.26156 = ${result || '9.19749'}`,
          result || '9.19749 pc'
        )
      };
    }

    // Furlongs to Links
    if (fromUnit === 'Furlongs' && toUnit === 'Links') {
      return {
        introduction: [
          "Converting from furlongs to links.",
          "Historical surveying units."
        ],
        formula: {
          main: "li = fur × 1000",
          alternate: "fur = li ÷ 1000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'fur',
          'li',
          'li = fur × 1000',
          `${valueToUse} × 1000 = ${result || '30000'}`,
          result || '30000 li'
        )
      };
    }

    // Links to Furlongs
    if (fromUnit === 'Links' && toUnit === 'Furlongs') {
      return {
        introduction: [
          "Converting from links to furlongs.",
          "Historical land measurement units."
        ],
        formula: {
          main: "fur = li ÷ 1000",
          alternate: "li = fur × 1000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'li',
          'fur',
          'fur = li ÷ 1000',
          `${valueToUse} ÷ 1000 = ${result || '0.03'}`,
          result || '0.03 fur'
        )
      };
    }

    // Yards to Fathoms
    if (fromUnit === 'Yards' && toUnit === 'Fathoms') {
      return {
        introduction: [
          "Converting from yards to fathoms.",
          "Used in maritime depth measurements."
        ],
        formula: {
          main: "ftm = yd ÷ 2",
          alternate: "yd = ftm × 2"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'yd',
          'ftm',
          'ftm = yd ÷ 2',
          `${valueToUse} ÷ 2 = ${result || '15'}`,
          result || '15 ftm'
        )
      };
    }

    // Nautical Miles to Meters
    if (fromUnit === 'Nautical Miles' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting from nautical miles to meters.",
          "Used in maritime navigation and aviation."
        ],
        formula: {
          main: "m = nmi × 1852",
          alternate: "nmi = m ÷ 1852"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'nmi',
          'm',
          'm = nmi × 1852',
          `${valueToUse} × 1852 = ${result || '55560'}`,
          result || '55560 m'
        )
      };
    }

    // Parsecs to Astronomical Units
    if (fromUnit === 'Parsecs' && toUnit === 'Astronomical Units') {
      return {
        introduction: [
          "Converting from parsecs to astronomical units.",
          "Used in astronomical measurements."
        ],
        formula: {
          main: "au = pc × 206265",
          alternate: "pc = au ÷ 206265"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'pc',
          'au',
          'au = pc × 206265',
          `${valueToUse} × 206265 = ${result || '6187950'}`,
          result || '6187950 au'
        )
      };
    }

    // Light Years to Kilometers
    if (fromUnit === 'Light Years' && toUnit === 'Kilometers') {
      return {
        introduction: [
          "Converting from light years to kilometers.",
          "Used in astronomical distances."
        ],
        formula: {
          main: "km = ly × 9.461e12",
          alternate: "ly = km ÷ 9.461e12"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'km',
          'km = ly × 9.461e12',
          `${valueToUse} × 9.461e12 = ${result || '2.8383e14'}`,
          result || '2.8383e14 km'
        )
      };
    }

    // Nanometers to Millimeters
    if (fromUnit === 'Nanometers' && toUnit === 'Millimeters') {
      return {
        introduction: [
          "Converting from nanometers to millimeters.",
          "Used in microscopic and precision measurements."
        ],
        formula: {
          main: "mm = nm ÷ 1000000",
          alternate: "nm = mm × 1000000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'nm',
          'mm',
          'mm = nm ÷ 1000000',
          `${valueToUse} ÷ 1000000 = ${result || '0.00003'}`,
          result || '0.00003 mm'
        )
      };
    }

    // Microns to Millimeters
    if (fromUnit === 'Microns' && toUnit === 'Millimeters') {
      return {
        introduction: [
          "Converting from microns to millimeters.",
          "Used in precision engineering and microscopy."
        ],
        formula: {
          main: "mm = μ ÷ 1000",
          alternate: "μ = mm × 1000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'μ',
          'mm',
          'mm = μ ÷ 1000',
          `${valueToUse} ÷ 1000 = ${result || '0.03'}`,
          result || '0.03 mm'
        )
      };
    }

    // Angstroms to Millimeters
    if (fromUnit === 'Angstroms' && toUnit === 'Millimeters') {
      return {
        introduction: [
          "Converting from angstroms to millimeters.",
          "Used in atomic and molecular measurements."
        ],
        formula: {
          main: "mm = Å ÷ 10000000",
          alternate: "Å = mm × 10000000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'mm',
          'mm = Å ÷ 10000000',
          `${valueToUse} ÷ 10000000 = ${result || '0.000003'}`,
          result || '0.000003 mm'
        )
      };
    }

    // Chains to Links
    if (fromUnit === 'Chains' && toUnit === 'Links') {
      return {
        introduction: [
          "Converting from chains to links.",
          "Historical surveying units."
        ],
        formula: {
          main: "li = ch × 100",
          alternate: "ch = li ÷ 100"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ch',
          'li',
          'li = ch × 100',
          `${valueToUse} × 100 = ${result || '3000'}`,
          result || '3000 li'
        )
      };
    }

    // Rods to Yards
    if (fromUnit === 'Rods' && toUnit === 'Yards') {
      return {
        introduction: [
          "Converting from rods to yards.",
          "Historical land measurement units."
        ],
        formula: {
          main: "yd = rd × 5.5",
          alternate: "rd = yd ÷ 5.5"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'rd',
          'yd',
          'yd = rd × 5.5',
          `${valueToUse} × 5.5 = ${result || '165'}`,
          result || '165 yd'
        )
      };
    }

    // Furlongs to Yards
    if (fromUnit === 'Furlongs' && toUnit === 'Yards') {
      return {
        introduction: [
          "Converting from furlongs to yards.",
          "Historical distance measurements."
        ],
        formula: {
          main: "yd = fur × 220",
          alternate: "fur = yd ÷ 220"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'fur',
          'yd',
          'yd = fur × 220',
          `${valueToUse} × 220 = ${result || '6600'}`,
          result || '6600 yd'
        )
      };
    }

    // Links to Meters
    if (fromUnit === 'Links' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting from links to meters.",
          "Historical surveying to metric conversion."
        ],
        formula: {
          main: "m = li × 0.201168",
          alternate: "li = m ÷ 0.201168"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'li',
          'm',
          'm = li × 0.201168',
          `${valueToUse} × 0.201168 = ${result || '6.03504'}`,
          result || '6.03504 m'
        )
      };
    }

    // Meters to Links
    if (fromUnit === 'Meters' && toUnit === 'Links') {
      return {
        introduction: [
          "Converting from meters to links.",
          "Metric to historical surveying conversion."
        ],
        formula: {
          main: "li = m ÷ 0.201168",
          alternate: "m = li × 0.201168"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'm',
          'li',
          'li = m ÷ 0.201168',
          `${valueToUse} ÷ 0.201168 = ${result || '149.13'}`,
          result || '149.13 li'
        )
      };
    }

    // Chains to Meters
    if (fromUnit === 'Chains' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting from chains to meters.",
          "Historical surveying to metric conversion."
        ],
        formula: {
          main: "m = ch × 20.1168",
          alternate: "ch = m ÷ 20.1168"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ch',
          'm',
          'm = ch × 20.1168',
          `${valueToUse} × 20.1168 = ${result || '603.504'}`,
          result || '603.504 m'
        )
      };
    }

    // Rods to Meters
    if (fromUnit === 'Rods' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting from rods to meters.",
          "Historical to metric conversion."
        ],
        formula: {
          main: "m = rd × 5.0292",
          alternate: "rd = m ÷ 5.0292"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'rd',
          'm',
          'm = rd × 5.0292',
          `${valueToUse} × 5.0292 = ${result || '150.876'}`,
          result || '150.876 m'
        )
      };
    }

    // Furlongs to Meters
    if (fromUnit === 'Furlongs' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting from furlongs to meters.",
          "Historical to metric conversion."
        ],
        formula: {
          main: "m = fur × 201.168",
          alternate: "fur = m ÷ 201.168"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'fur',
          'm',
          'm = fur × 201.168',
          `${valueToUse} × 201.168 = ${result || '6035.04'}`,
          result || '6035.04 m'
        )
      };
    }

    // Meters to Chains (reverse)
    if (fromUnit === 'Meters' && toUnit === 'Chains') {
      return {
        introduction: [
          "Converting from meters to chains.",
          "Metric to historical surveying conversion."
        ],
        formula: {
          main: "ch = m ÷ 20.1168",
          alternate: "m = ch × 20.1168"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'm',
          'ch',
          'ch = m ÷ 20.1168',
          `${valueToUse} ÷ 20.1168 = ${result || '1.49133'}`,
          result || '1.49133 ch'
        )
      };
    }

    // Parsecs to Light Years (reverse of existing)
    if (fromUnit === 'Parsecs' && toUnit === 'Light Years') {
      return {
        introduction: [
          "Converting from parsecs to light years.",
          "Used in astronomical measurements."
        ],
        formula: {
          main: "ly = pc × 3.26156",
          alternate: "pc = ly ÷ 3.26156"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'pc',
          'ly',
          'ly = pc × 3.26156',
          `${valueToUse} × 3.26156 = ${result || '97.8468'}`,
          result || '97.8468 ly'
        )
      };
    }

    // Kilometers to Light Years (reverse of existing)
    if (fromUnit === 'Kilometers' && toUnit === 'Light Years') {
      return {
        introduction: [
          "Converting from kilometers to light years.",
          "Used in astronomical distances."
        ],
        formula: {
          main: "ly = km ÷ 9.461e12",
          alternate: "km = ly × 9.461e12"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'km',
          'ly',
          'ly = km ÷ 9.461e12',
          `${valueToUse} ÷ 9.461e12 = ${result || '3.17e-12'}`,
          result || '3.17e-12 ly'
        )
      };
    }

    // Micrometers to Nanometers (reverse of existing)
    if (fromUnit === 'Micrometers' && toUnit === 'Nanometers') {
      return {
        introduction: [
          "Converting from micrometers to nanometers.",
          "Used in microscopic measurements."
        ],
        formula: {
          main: "nm = μm × 1000",
          alternate: "μm = nm ÷ 1000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'μm',
          'nm',
          'nm = μm × 1000',
          `${valueToUse} × 1000 = ${result || '30000'}`,
          result || '30000 nm'
        )
      };
    }

    // Millimeters to Inches (reverse of existing)
    if (fromUnit === 'Millimeters' && toUnit === 'Inches') {
      return {
        introduction: [
          "Converting from millimeters to inches.",
          "Used in engineering and technical specifications."
        ],
        formula: {
          main: "in = mm ÷ 25.4",
          alternate: "mm = in × 25.4"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mm',
          'in',
          'in = mm ÷ 25.4',
          `${valueToUse} ÷ 25.4 = ${result || '1.1811'}`,
          result || '1.1811 in'
        )
      };
    }

    // Inches to Meters
    if (fromUnit === 'Inches' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting from inches to meters.",
          "Common in international engineering and manufacturing."
        ],
        formula: {
          main: "m = in × 0.0254",
          alternate: "in = m ÷ 0.0254"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'in',
          'm',
          'm = in × 0.0254',
          `${valueToUse} × 0.0254 = ${result || '0.762'}`,
          result || '0.762 m'
        )
      };
    }

    // Chains to Yards
    if (fromUnit === 'Chains' && toUnit === 'Yards') {
      return {
        introduction: [
          "Converting from chains to yards.",
          "Used in land surveying."
        ],
        formula: {
          main: "yd = ch × 22",
          alternate: "ch = yd ÷ 22"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ch',
          'yd',
          'yd = ch × 22',
          `${valueToUse} × 22 = ${result || '660'}`,
          result || '660 yd'
        )
      };
    }

    // Meters to Rods (reverse of existing)
    if (fromUnit === 'Meters' && toUnit === 'Rods') {
      return {
        introduction: [
          "Converting from meters to rods.",
          "Metric to historical surveying conversion."
        ],
        formula: {
          main: "rd = m ÷ 5.0292",
          alternate: "m = rd × 5.0292"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'm',
          'rd',
          'rd = m ÷ 5.0292',
          `${valueToUse} ÷ 5.0292 = ${result || '5.965'}`,
          result || '5.965 rd'
        )
      };
    }

    // Yards to Chains (reverse of existing)
    if (fromUnit === 'Yards' && toUnit === 'Chains') {
      return {
        introduction: [
          "Converting from yards to chains.",
          "Historical surveying units."
        ],
        formula: {
          main: "ch = yd ÷ 22",
          alternate: "yd = ch × 22"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'yd',
          'ch',
          'ch = yd ÷ 22',
          `${valueToUse} ÷ 22 = ${result || '1.364'}`,
          result || '1.364 ch'
        )
      };
    }

    // Meters to Furlongs (reverse of existing)
    if (fromUnit === 'Meters' && toUnit === 'Furlongs') {
      return {
        introduction: [
          "Converting from meters to furlongs.",
          "Metric to historical conversion."
        ],
        formula: {
          main: "fur = m ÷ 201.168",
          alternate: "m = fur × 201.168"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'm',
          'fur',
          'fur = m ÷ 201.168',
          `${valueToUse} ÷ 201.168 = ${result || '0.149'}`,
          result || '0.149 fur'
        )
      };
    }

    // Light Years to Meters
    if (fromUnit === 'Light Years' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting from light years to meters.",
          "Used in astronomical distances."
        ],
        formula: {
          main: "m = ly × 9.461e15",
          alternate: "ly = m ÷ 9.461e15"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'm',
          'm = ly × 9.461e15',
          `${valueToUse} × 9.461e15 = ${result || '2.838e17'}`,
          result || '2.838e17 m'
        )
      };
    }

    // Astronomical Units to Meters
    if (fromUnit === 'Astronomical Units' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting from astronomical units to meters.",
          "Used in solar system measurements."
        ],
        formula: {
          main: "m = au × 1.496e11",
          alternate: "au = m ÷ 1.496e11"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'm',
          'm = au × 1.496e11',
          `${valueToUse} × 1.496e11 = ${result || '4.488e12'}`,
          result || '4.488e12 m'
        )
      };
    }

    // Meters to Astronomical Units
    if (fromUnit === 'Meters' && toUnit === 'Astronomical Units') {
      return {
        introduction: [
          "Converting from meters to astronomical units.",
          "Used in solar system measurements."
        ],
        formula: {
          main: "au = m ÷ 1.496e11",
          alternate: "m = au × 1.496e11"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'm',
          'au',
          'au = m ÷ 1.496e11',
          `${valueToUse} ÷ 1.496e11 = ${result || '2.006e-10'}`,
          result || '2.006e-10 au'
        )
      };
    }

    // Nanometers to Angstroms (reverse of existing)
    if (fromUnit === 'Nanometers' && toUnit === 'Angstroms') {
      return {
        introduction: [
          "Converting from nanometers to angstroms.",
          "Used in atomic and molecular measurements."
        ],
        formula: {
          main: "Å = nm × 10",
          alternate: "nm = Å ÷ 10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'nm',
          'Å',
          'Å = nm × 10',
          `${valueToUse} × 10 = ${result || '300'}`,
          result || '300 Å'
        )
      };
    }

    // Miles to Furlongs (reverse of existing)
    if (fromUnit === 'Miles' && toUnit === 'Furlongs') {
      return {
        introduction: [
          "Converting from miles to furlongs.",
          "Historical distance measurements."
        ],
        formula: {
          main: "fur = mi × 8",
          alternate: "mi = fur ÷ 8"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'mi',
          'fur',
          'fur = mi × 8',
          `${valueToUse} × 8 = ${result || '240'}`,
          result || '240 fur'
        )
      };
    }

    // Chains to Furlongs (reverse of existing)
    if (fromUnit === 'Chains' && toUnit === 'Furlongs') {
      return {
        introduction: [
          "Converting from chains to furlongs.",
          "Historical land measurement units."
        ],
        formula: {
          main: "fur = ch ÷ 10",
          alternate: "ch = fur × 10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ch',
          'fur',
          'fur = ch ÷ 10',
          `${valueToUse} ÷ 10 = ${result || '3'}`,
          result || '3 fur'
        )
      };
    }

    // Astronomical Units to Light Years
    if (fromUnit === 'Astronomical Units' && toUnit === 'Light Years') {
      return {
        introduction: [
          "Converting from astronomical units to light years.",
          "Used in astronomical measurements."
        ],
        formula: {
          main: "ly = au ÷ 63241.1",
          alternate: "au = ly × 63241.1"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'ly',
          'ly = au ÷ 63241.1',
          `${valueToUse} ÷ 63241.1 = ${result || '0.000474'}`,
          result || '0.000474 ly'
        )
      };
    }

    // Light Years to Astronomical Units
    if (fromUnit === 'Light Years' && toUnit === 'Astronomical Units') {
      return {
        introduction: [
          "Converting from light years to astronomical units.",
          "Used in astronomical measurements."
        ],
        formula: {
          main: "au = ly × 63241.1",
          alternate: "ly = au ÷ 63241.1"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'au',
          'au = ly × 63241.1',
          `${valueToUse} × 63241.1 = ${result || '1897233'}`,
          result || '1897233 au'
        )
      };
    }

    // Yards to Rods
    if (fromUnit === 'Yards' && toUnit === 'Rods') {
      return {
        introduction: [
          "Converting from yards to rods.",
          "Historical surveying units."
        ],
        formula: {
          main: "rd = yd ÷ 5.5",
          alternate: "yd = rd × 5.5"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'yd',
          'rd',
          'rd = yd ÷ 5.5',
          `${valueToUse} ÷ 5.5 = ${result || '5.45'}`,
          result || '5.45 rd'
        )
      };
    }

    // Rods to Links
    if (fromUnit === 'Rods' && toUnit === 'Links') {
      return {
        introduction: [
          "Converting from rods to links.",
          "Historical surveying units."
        ],
        formula: {
          main: "li = rd × 25",
          alternate: "rd = li ÷ 25"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'rd',
          'li',
          'li = rd × 25',
          `${valueToUse} × 25 = ${result || '750'}`,
          result || '750 li'
        )
      };
    }

    // Centimeters to Meters
    if (fromUnit === 'Centimeters' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting from centimeters to meters.",
          "Basic metric system conversion."
        ],
        formula: {
          main: "m = cm ÷ 100",
          alternate: "cm = m × 100"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'm',
          'm = cm ÷ 100',
          `${valueToUse} ÷ 100 = ${result || '0.3'}`,
          result || '0.3 m'
        )
      };
    }

    // Centimeters to Inches
    if (fromUnit === 'Centimeters' && toUnit === 'Inches') {
      return {
        introduction: [
          "Converting from centimeters to inches.",
          "Common metric to imperial conversion."
        ],
        formula: {
          main: "in = cm ÷ 2.54",
          alternate: "cm = in × 2.54"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'in',
          'in = cm ÷ 2.54',
          `${valueToUse} ÷ 2.54 = ${result || '11.811'}`,
          result || '11.811 in'
        )
      };
    }

    // Centimeters to Feet
    if (fromUnit === 'Centimeters' && toUnit === 'Feet') {
      return {
        introduction: [
          "Converting from centimeters to feet.",
          "Metric to imperial conversion."
        ],
        formula: {
          main: "ft = cm ÷ 30.48",
          alternate: "cm = ft × 30.48"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'ft',
          'ft = cm ÷ 30.48',
          `${valueToUse} ÷ 30.48 = ${result || '0.984'}`,
          result || '0.984 ft'
        )
      };
    }

    // Centimeters to Millimeters
    if (fromUnit === 'Centimeters' && toUnit === 'Millimeters') {
      return {
        introduction: [
          "Converting from centimeters to millimeters.",
          "Basic metric system conversion."
        ],
        formula: {
          main: "mm = cm × 10",
          alternate: "cm = mm ÷ 10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'mm',
          'mm = cm × 10',
          `${valueToUse} × 10 = ${result || '300'}`,
          result || '300 mm'
        )
      };
    }

    // Centimeters to Kilometers
    if (fromUnit === 'Centimeters' && toUnit === 'Kilometers') {
      return {
        introduction: [
          "Converting from centimeters to kilometers.",
          "Metric system conversion."
        ],
        formula: {
          main: "km = cm ÷ 100000",
          alternate: "cm = km × 100000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'km',
          'km = cm ÷ 100000',
          `${valueToUse} ÷ 100000 = ${result || '0.0003'}`,
          result || '0.0003 km'
        )
      };
    }

    // Centimeters to Yards
    if (fromUnit === 'Centimeters' && toUnit === 'Yards') {
      return {
        introduction: [
          "Converting from centimeters to yards.",
          "Metric to imperial conversion."
        ],
        formula: {
          main: "yd = cm ÷ 91.44",
          alternate: "cm = yd × 91.44"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'yd',
          'yd = cm ÷ 91.44',
          `${valueToUse} ÷ 91.44 = ${result || '0.328'}`,
          result || '0.328 yd'
        )
      };
    }

    // Centimeters to Miles
    if (fromUnit === 'Centimeters' && toUnit === 'Miles') {
      return {
        introduction: [
          "Converting from centimeters to miles.",
          "Metric to imperial distance conversion."
        ],
        formula: {
          main: "mi = cm ÷ 160934",
          alternate: "cm = mi × 160934"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'mi',
          'mi = cm ÷ 160934',
          `${valueToUse} ÷ 160934 = ${result || '0.000186'}`,
          result || '0.000186 mi'
        )
      };
    }

    // Centimeters to Nanometers
    if (fromUnit === 'Centimeters' && toUnit === 'Nanometers') {
      return {
        introduction: [
          "Converting from centimeters to nanometers.",
          "Metric system conversion for microscopic measurements."
        ],
        formula: {
          main: "nm = cm × 10000000",
          alternate: "cm = nm ÷ 10000000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'nm',
          'nm = cm × 10000000',
          `${valueToUse} × 10000000 = ${result || '300000000'}`,
          result || '300000000 nm'
        )
      };
    }

    // Centimeters to Micrometers
    if (fromUnit === 'Centimeters' && toUnit === 'Micrometers') {
      return {
        introduction: [
          "Converting from centimeters to micrometers.",
          "Metric system conversion for precision measurements."
        ],
        formula: {
          main: "μm = cm × 10000",
          alternate: "cm = μm ÷ 10000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'μm',
          'μm = cm × 10000',
          `${valueToUse} × 10000 = ${result || '300000'}`,
          result || '300000 μm'
        )
      };
    }

    // Centimeters to Nautical Miles
    if (fromUnit === 'Centimeters' && toUnit === 'Nautical Miles') {
      return {
        introduction: [
          "Converting from centimeters to nautical miles.",
          "Used in maritime and aviation measurements."
        ],
        formula: {
          main: "nmi = cm ÷ 185200",
          alternate: "cm = nmi × 185200"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'nmi',
          'nmi = cm ÷ 185200',
          `${valueToUse} ÷ 185200 = ${result || '0.000162'}`,
          result || '0.000162 nmi'
        )
      };
    }

    // Centimeters to Angstroms
    if (fromUnit === 'Centimeters' && toUnit === 'Angstroms') {
      return {
        introduction: [
          "Converting from centimeters to angstroms.",
          "Used in atomic and molecular measurements."
        ],
        formula: {
          main: "Å = cm × 100000000",
          alternate: "cm = Å ÷ 100000000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'Å',
          'Å = cm × 100000000',
          `${valueToUse} × 100000000 = ${result || '3000000000'}`,
          result || '3000000000 Å'
        )
      };
    }

    // Centimeters to Microns
    if (fromUnit === 'Centimeters' && toUnit === 'Microns') {
      return {
        introduction: [
          "Converting from centimeters to microns.",
          "Used in microscopic measurements."
        ],
        formula: {
          main: "μ = cm × 10000",
          alternate: "cm = μ ÷ 10000"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'μ',
          'μ = cm × 10000',
          `${valueToUse} × 10000 = ${result || '300000'}`,
          result || '300000 μ'
        )
      };
    }

    // Centimeters to Mils
    if (fromUnit === 'Centimeters' && toUnit === 'Mils') {
      return {
        introduction: [
          "Converting from centimeters to mils.",
          "Used in engineering and manufacturing."
        ],
        formula: {
          main: "mil = cm × 393.701",
          alternate: "cm = mil ÷ 393.701"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'mil',
          'mil = cm × 393.701',
          `${valueToUse} × 393.701 = ${result || '11811.03'}`,
          result || '11811.03 mil'
        )
      };
    }

    // Centimeters to Fathoms
    if (fromUnit === 'Centimeters' && toUnit === 'Fathoms') {
      return {
        introduction: [
          "Converting from centimeters to fathoms.",
          "Used in maritime depth measurements."
        ],
        formula: {
          main: "ftm = cm ÷ 182.88",
          alternate: "cm = ftm × 182.88"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'ftm',
          'ftm = cm ÷ 182.88',
          `${valueToUse} ÷ 182.88 = ${result || '0.164'}`,
          result || '0.164 ftm'
        )
      };
    }

    // Centimeters to Furlongs
    if (fromUnit === 'Centimeters' && toUnit === 'Furlongs') {
      return {
        introduction: [
          "Converting from centimeters to furlongs.",
          "Historical distance measurement."
        ],
        formula: {
          main: "fur = cm ÷ 20116.8",
          alternate: "cm = fur × 20116.8"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'fur',
          'fur = cm ÷ 20116.8',
          `${valueToUse} ÷ 20116.8 = ${result || '0.00149'}`,
          result || '0.00149 fur'
        )
      };
    }

    // Centimeters to Chains
    if (fromUnit === 'Centimeters' && toUnit === 'Chains') {
      return {
        introduction: [
          "Converting from centimeters to chains.",
          "Historical surveying measurement."
        ],
        formula: {
          main: "ch = cm ÷ 2011.68",
          alternate: "cm = ch × 2011.68"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'ch',
          'ch = cm ÷ 2011.68',
          `${valueToUse} ÷ 2011.68 = ${result || '0.0149'}`,
          result || '0.0149 ch'
        )
      };
    }

    // Centimeters to Rods
    if (fromUnit === 'Centimeters' && toUnit === 'Rods') {
      return {
        introduction: [
          "Converting from centimeters to rods.",
          "Historical surveying measurement."
        ],
        formula: {
          main: "rd = cm ÷ 502.92",
          alternate: "cm = rd × 502.92"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'rd',
          'rd = cm ÷ 502.92',
          `${valueToUse} ÷ 502.92 = ${result || '0.0596'}`,
          result || '0.0596 rd'
        )
      };
    }

    // Centimeters to Links
    if (fromUnit === 'Centimeters' && toUnit === 'Links') {
      return {
        introduction: [
          "Converting from centimeters to links.",
          "Historical surveying measurement."
        ],
        formula: {
          main: "li = cm ÷ 20.1168",
          alternate: "cm = li × 20.1168"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'li',
          'li = cm ÷ 20.1168',
          `${valueToUse} ÷ 20.1168 = ${result || '1.4913'}`,
          result || '1.4913 li'
        )
      };
    }

    // Centimeters to Parsecs
    if (fromUnit === 'Centimeters' && toUnit === 'Parsecs') {
      return {
        introduction: [
          "Converting from centimeters to parsecs.",
          "Astronomical distance measurement."
        ],
        formula: {
          main: "pc = cm ÷ 3.086e18",
          alternate: "cm = pc × 3.086e18"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'cm',
          'pc',
          'pc = cm ÷ 3.086e18',
          `${valueToUse} ÷ 3.086e18 = ${result || '9.72e-19'}`,
          result || '9.72e-19 pc'
        )
      };
    }

    // Light Years to Centimeters
    if (fromUnit === 'Light Years' && toUnit === 'Centimeters') {
      return {
        introduction: [
          "Converting from light years to centimeters.",
          "Astronomical to metric conversion."
        ],
        formula: {
          main: "cm = ly × 9.461e17",
          alternate: "ly = cm ÷ 9.461e17"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'cm',
          'cm = ly × 9.461e17',
          `${valueToUse} × 9.461e17 = ${result || '2.838e19'}`,
          result || '2.838e19 cm'
        )
      };
    }

    // Light Years to Millimeters
    if (fromUnit === 'Light Years' && toUnit === 'Millimeters') {
      return {
        introduction: [
          "Converting from light years to millimeters.",
          "Astronomical to metric conversion."
        ],
        formula: {
          main: "mm = ly × 9.461e18",
          alternate: "ly = mm ÷ 9.461e18"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'mm',
          'mm = ly × 9.461e18',
          `${valueToUse} × 9.461e18 = ${result || '2.838e20'}`,
          result || '2.838e20 mm'
        )
      };
    }

    // Light Years to Miles
    if (fromUnit === 'Light Years' && toUnit === 'Miles') {
      return {
        introduction: [
          "Converting from light years to miles.",
          "Astronomical to imperial conversion."
        ],
        formula: {
          main: "mi = ly × 5.879e12",
          alternate: "ly = mi ÷ 5.879e12"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'mi',
          'mi = ly × 5.879e12',
          `${valueToUse} × 5.879e12 = ${result || '1.764e14'}`,
          result || '1.764e14 mi'
        )
      };
    }

    // Light Years to Nautical Miles
    if (fromUnit === 'Light Years' && toUnit === 'Nautical Miles') {
      return {
        introduction: [
          "Converting from light years to nautical miles.",
          "Astronomical to maritime conversion."
        ],
        formula: {
          main: "nmi = ly × 5.108e12",
          alternate: "ly = nmi ÷ 5.108e12"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'nmi',
          'nmi = ly × 5.108e12',
          `${valueToUse} × 5.108e12 = ${result || '1.532e14'}`,
          result || '1.532e14 nmi'
        )
      };
    }

    // Light Years to Yards
    if (fromUnit === 'Light Years' && toUnit === 'Yards') {
      return {
        introduction: [
          "Converting from light years to yards.",
          "Astronomical to imperial conversion."
        ],
        formula: {
          main: "yd = ly × 1.035e13",
          alternate: "ly = yd ÷ 1.035e13"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'yd',
          'yd = ly × 1.035e13',
          `${valueToUse} × 1.035e13 = ${result || '3.105e14'}`,
          result || '3.105e14 yd'
        )
      };
    }

    // Light Years to Feet
    if (fromUnit === 'Light Years' && toUnit === 'Feet') {
      return {
        introduction: [
          "Converting from light years to feet.",
          "Astronomical to imperial conversion."
        ],
        formula: {
          main: "ft = ly × 3.104e13",
          alternate: "ly = ft ÷ 3.104e13"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'ft',
          'ft = ly × 3.104e13',
          `${valueToUse} × 3.104e13 = ${result || '9.312e14'}`,
          result || '9.312e14 ft'
        )
      };
    }

    // Light Years to Inches
    if (fromUnit === 'Light Years' && toUnit === 'Inches') {
      return {
        introduction: [
          "Converting from light years to inches.",
          "Astronomical to imperial conversion."
        ],
        formula: {
          main: "in = ly × 3.725e14",
          alternate: "ly = in ÷ 3.725e14"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'in',
          'in = ly × 3.725e14',
          `${valueToUse} × 3.725e14 = ${result || '1.117e16'}`,
          result || '1.117e16 in'
        )
      };
    }

    // Light Years to Micrometers
    if (fromUnit === 'Light Years' && toUnit === 'Micrometers') {
      return {
        introduction: [
          "Converting from light years to micrometers.",
          "Astronomical to microscopic measurement."
        ],
        formula: {
          main: "μm = ly × 9.461e21",
          alternate: "ly = μm ÷ 9.461e21"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'μm',
          'μm = ly × 9.461e21',
          `${valueToUse} × 9.461e21 = ${result || '2.838e23'}`,
          result || '2.838e23 μm'
        )
      };
    }

    // Light Years to Nanometers
    if (fromUnit === 'Light Years' && toUnit === 'Nanometers') {
      return {
        introduction: [
          "Converting from light years to nanometers.",
          "Astronomical to microscopic measurement."
        ],
        formula: {
          main: "nm = ly × 9.461e24",
          alternate: "ly = nm ÷ 9.461e24"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'nm',
          'nm = ly × 9.461e24',
          `${valueToUse} × 9.461e24 = ${result || '2.838e26'}`,
          result || '2.838e26 nm'
        )
      };
    }

    // Light Years to Angstroms
    if (fromUnit === 'Light Years' && toUnit === 'Angstroms') {
      return {
        introduction: [
          "Converting from light years to angstroms.",
          "Astronomical to atomic scale measurement."
        ],
        formula: {
          main: "Å = ly × 9.461e25",
          alternate: "ly = Å ÷ 9.461e25"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'Å',
          'Å = ly × 9.461e25',
          `${valueToUse} × 9.461e25 = ${result || '2.838e27'}`,
          result || '2.838e27 Å'
        )
      };
    }

    // Light Years to Microns
    if (fromUnit === 'Light Years' && toUnit === 'Microns') {
      return {
        introduction: [
          "Converting from light years to microns.",
          "Astronomical to microscopic measurement."
        ],
        formula: {
          main: "μ = ly × 9.461e21",
          alternate: "ly = μ ÷ 9.461e21"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'μ',
          'μ = ly × 9.461e21',
          `${valueToUse} × 9.461e21 = ${result || '2.838e23'}`,
          result || '2.838e23 μ'
        )
      };
    }

    // Light Years to Mils
    if (fromUnit === 'Light Years' && toUnit === 'Mils') {
      return {
        introduction: [
          "Converting from light years to mils.",
          "Astronomical to engineering measurement."
        ],
        formula: {
          main: "mil = ly × 3.725e16",
          alternate: "ly = mil ÷ 3.725e16"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'mil',
          'mil = ly × 3.725e16',
          `${valueToUse} × 3.725e16 = ${result || '1.117e18'}`,
          result || '1.117e18 mil'
        )
      };
    }

    // Light Years to Fathoms
    if (fromUnit === 'Light Years' && toUnit === 'Fathoms') {
      return {
        introduction: [
          "Converting from light years to fathoms.",
          "Astronomical to maritime measurement."
        ],
        formula: {
          main: "ftm = ly × 5.175e12",
          alternate: "ly = ftm ÷ 5.175e12"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'ftm',
          'ftm = ly × 5.175e12',
          `${valueToUse} × 5.175e12 = ${result || '1.553e14'}`,
          result || '1.553e14 ftm'
        )
      };
    }

    // Light Years to Furlongs
    if (fromUnit === 'Light Years' && toUnit === 'Furlongs') {
      return {
        introduction: [
          "Converting from light years to furlongs.",
          "Astronomical to historical measurement."
        ],
        formula: {
          main: "fur = ly × 4.702e10",
          alternate: "ly = fur ÷ 4.702e10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'fur',
          'fur = ly × 4.702e10',
          `${valueToUse} × 4.702e10 = ${result || '1.411e12'}`,
          result || '1.411e12 fur'
        )
      };
    }

    // Light Years to Chains
    if (fromUnit === 'Light Years' && toUnit === 'Chains') {
      return {
        introduction: [
          "Converting from light years to chains.",
          "Astronomical to surveying measurement."
        ],
        formula: {
          main: "ch = ly × 4.702e11",
          alternate: "ly = ch ÷ 4.702e11"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'ch',
          'ch = ly × 4.702e11',
          `${valueToUse} × 4.702e11 = ${result || '1.411e13'}`,
          result || '1.411e13 ch'
        )
      };
    }

    // Light Years to Rods
    if (fromUnit === 'Light Years' && toUnit === 'Rods') {
      return {
        introduction: [
          "Converting from light years to rods.",
          "Astronomical to surveying measurement."
        ],
        formula: {
          main: "rd = ly × 1.881e12",
          alternate: "ly = rd ÷ 1.881e12"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'rd',
          'rd = ly × 1.881e12',
          `${valueToUse} × 1.881e12 = ${result || '5.643e13'}`,
          result || '5.643e13 rd'
        )
      };
    }

    // Light Years to Links
    if (fromUnit === 'Light Years' && toUnit === 'Links') {
      return {
        introduction: [
          "Converting from light years to links.",
          "Astronomical to surveying measurement."
        ],
        formula: {
          main: "li = ly × 4.702e13",
          alternate: "ly = li ÷ 4.702e13"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'ly',
          'li',
          'li = ly × 4.702e13',
          `${valueToUse} × 4.702e13 = ${result || '1.411e15'}`,
          result || '1.411e15 li'
        )
      };
    }

    // Astronomical Units to Kilometers
    if (fromUnit === 'Astronomical Units' && toUnit === 'Kilometers') {
      return {
        introduction: [
          "Converting from astronomical units to kilometers.",
          "Used in solar system measurements."
        ],
        formula: {
          main: "km = au × 1.496e8",
          alternate: "au = km ÷ 1.496e8"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'km',
          'km = au × 1.496e8',
          `${valueToUse} × 1.496e8 = ${result || '4.488e9'}`,
          result || '4.488e9 km'
        )
      };
    }

    // Astronomical Units to Light Years
    if (fromUnit === 'Astronomical Units' && toUnit === 'Light Years') {
      return {
        introduction: [
          "Converting from astronomical units to light years.",
          "Common astronomical distance conversion."
        ],
        formula: {
          main: "ly = au ÷ 63241.1",
          alternate: "au = ly × 63241.1"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'ly',
          'ly = au ÷ 63241.1',
          `${valueToUse} ÷ 63241.1 = ${result || '0.000474'}`,
          result || '0.000474 ly'
        )
      };
    }

    // Astronomical Units to Parsecs
    if (fromUnit === 'Astronomical Units' && toUnit === 'Parsecs') {
      return {
        introduction: [
          "Converting from astronomical units to parsecs.",
          "Used in stellar distance measurements."
        ],
        formula: {
          main: "pc = au ÷ 206265",
          alternate: "au = pc × 206265"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'pc',
          'pc = au ÷ 206265',
          `${valueToUse} ÷ 206265 = ${result || '0.000145'}`,
          result || '0.000145 pc'
        )
      };
    }

    // Astronomical Units to Centimeters
    if (fromUnit === 'Astronomical Units' && toUnit === 'Centimeters') {
      return {
        introduction: [
          "Converting from astronomical units to centimeters.",
          "Astronomical to metric conversion."
        ],
        formula: {
          main: "cm = au × 1.496e13",
          alternate: "au = cm ÷ 1.496e13"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'cm',
          'cm = au × 1.496e13',
          `${valueToUse} × 1.496e13 = ${result || '4.488e14'}`,
          result || '4.488e14 cm'
        )
      };
    }

    // Astronomical Units to Millimeters
    if (fromUnit === 'Astronomical Units' && toUnit === 'Millimeters') {
      return {
        introduction: [
          "Converting from astronomical units to millimeters.",
          "Astronomical to metric conversion."
        ],
        formula: {
          main: "mm = au × 1.496e14",
          alternate: "au = mm ÷ 1.496e14"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'mm',
          'mm = au × 1.496e14',
          `${valueToUse} × 1.496e14 = ${result || '4.488e15'}`,
          result || '4.488e15 mm'
        )
      };
    }

    // Astronomical Units to Micrometers
    if (fromUnit === 'Astronomical Units' && toUnit === 'Micrometers') {
      return {
        introduction: [
          "Converting from astronomical units to micrometers.",
          "Astronomical to microscopic measurement."
        ],
        formula: {
          main: "μm = au × 1.496e17",
          alternate: "au = μm ÷ 1.496e17"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'μm',
          'μm = au × 1.496e17',
          `${valueToUse} × 1.496e17 = ${result || '4.488e18'}`,
          result || '4.488e18 μm'
        )
      };
    }

    // Astronomical Units to Nanometers
    if (fromUnit === 'Astronomical Units' && toUnit === 'Nanometers') {
      return {
        introduction: [
          "Converting from astronomical units to nanometers.",
          "Astronomical to microscopic measurement."
        ],
        formula: {
          main: "nm = au × 1.496e20",
          alternate: "au = nm ÷ 1.496e20"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'nm',
          'nm = au × 1.496e20',
          `${valueToUse} × 1.496e20 = ${result || '4.488e21'}`,
          result || '4.488e21 nm'
        )
      };
    }

    // Astronomical Units to Angstroms
    if (fromUnit === 'Astronomical Units' && toUnit === 'Angstroms') {
      return {
        introduction: [
          "Converting from astronomical units to angstroms.",
          "Astronomical to atomic scale measurement."
        ],
        formula: {
          main: "Å = au × 1.496e21",
          alternate: "au = Å ÷ 1.496e21"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'Å',
          'Å = au × 1.496e21',
          `${valueToUse} × 1.496e21 = ${result || '4.488e22'}`,
          result || '4.488e22 Å'
        )
      };
    }

    // Astronomical Units to Microns
    if (fromUnit === 'Astronomical Units' && toUnit === 'Microns') {
      return {
        introduction: [
          "Converting from astronomical units to microns.",
          "Astronomical to microscopic measurement."
        ],
        formula: {
          main: "μ = au × 1.496e17",
          alternate: "au = μ ÷ 1.496e17"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'μ',
          'μ = au × 1.496e17',
          `${valueToUse} × 1.496e17 = ${result || '4.488e18'}`,
          result || '4.488e18 μ'
        )
      };
    }

    // Astronomical Units to Miles
    if (fromUnit === 'Astronomical Units' && toUnit === 'Miles') {
      return {
        introduction: [
          "Converting from astronomical units to miles.",
          "Astronomical to imperial conversion."
        ],
        formula: {
          main: "mi = au × 9.296e7",
          alternate: "au = mi ÷ 9.296e7"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'mi',
          'mi = au × 9.296e7',
          `${valueToUse} × 9.296e7 = ${result || '2.789e9'}`,
          result || '2.789e9 mi'
        )
      };
    }

    // Astronomical Units to Nautical Miles
    if (fromUnit === 'Astronomical Units' && toUnit === 'Nautical Miles') {
      return {
        introduction: [
          "Converting from astronomical units to nautical miles.",
          "Astronomical to maritime measurement."
        ],
        formula: {
          main: "nmi = au × 8.078e7",
          alternate: "au = nmi ÷ 8.078e7"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'nmi',
          'nmi = au × 8.078e7',
          `${valueToUse} × 8.078e7 = ${result || '2.423e9'}`,
          result || '2.423e9 nmi'
        )
      };
    }

    // Astronomical Units to Yards
    if (fromUnit === 'Astronomical Units' && toUnit === 'Yards') {
      return {
        introduction: [
          "Converting from astronomical units to yards.",
          "Astronomical to imperial conversion."
        ],
        formula: {
          main: "yd = au × 1.636e11",
          alternate: "au = yd ÷ 1.636e11"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'yd',
          'yd = au × 1.636e11',
          `${valueToUse} × 1.636e11 = ${result || '4.908e12'}`,
          result || '4.908e12 yd'
        )
      };
    }

    // Astronomical Units to Feet
    if (fromUnit === 'Astronomical Units' && toUnit === 'Feet') {
      return {
        introduction: [
          "Converting from astronomical units to feet.",
          "Astronomical to imperial conversion."
        ],
        formula: {
          main: "ft = au × 4.908e11",
          alternate: "au = ft ÷ 4.908e11"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'ft',
          'ft = au × 4.908e11',
          `${valueToUse} × 4.908e11 = ${result || '1.472e13'}`,
          result || '1.472e13 ft'
        )
      };
    }

    // Astronomical Units to Inches
    if (fromUnit === 'Astronomical Units' && toUnit === 'Inches') {
      return {
        introduction: [
          "Converting from astronomical units to inches.",
          "Astronomical to imperial conversion."
        ],
        formula: {
          main: "in = au × 5.890e12",
          alternate: "au = in ÷ 5.890e12"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'in',
          'in = au × 5.890e12',
          `${valueToUse} × 5.890e12 = ${result || '1.767e14'}`,
          result || '1.767e14 in'
        )
      };
    }

    // Astronomical Units to Mils
    if (fromUnit === 'Astronomical Units' && toUnit === 'Mils') {
      return {
        introduction: [
          "Converting from astronomical units to mils.",
          "Astronomical to engineering measurement."
        ],
        formula: {
          main: "mil = au × 5.890e14",
          alternate: "au = mil ÷ 5.890e14"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'mil',
          'mil = au × 5.890e14',
          `${valueToUse} × 5.890e14 = ${result || '1.767e16'}`,
          result || '1.767e16 mil'
        )
      };
    }

    // Astronomical Units to Fathoms
    if (fromUnit === 'Astronomical Units' && toUnit === 'Fathoms') {
      return {
        introduction: [
          "Converting from astronomical units to fathoms.",
          "Astronomical to maritime measurement."
        ],
        formula: {
          main: "ftm = au × 8.180e10",
          alternate: "au = ftm ÷ 8.180e10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'ftm',
          'ftm = au × 8.180e10',
          `${valueToUse} × 8.180e10 = ${result || '2.454e12'}`,
          result || '2.454e12 ftm'
        )
      };
    }

    // Astronomical Units to Furlongs
    if (fromUnit === 'Astronomical Units' && toUnit === 'Furlongs') {
      return {
        introduction: [
          "Converting from astronomical units to furlongs.",
          "Astronomical to historical measurement."
        ],
        formula: {
          main: "fur = au × 7.437e8",
          alternate: "au = fur ÷ 7.437e8"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'fur',
          'fur = au × 7.437e8',
          `${valueToUse} × 7.437e8 = ${result || '2.231e10'}`,
          result || '2.231e10 fur'
        )
      };
    }

    // Astronomical Units to Chains
    if (fromUnit === 'Astronomical Units' && toUnit === 'Chains') {
      return {
        introduction: [
          "Converting from astronomical units to chains.",
          "Astronomical to surveying measurement."
        ],
        formula: {
          main: "ch = au × 7.437e9",
          alternate: "au = ch ÷ 7.437e9"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'ch',
          'ch = au × 7.437e9',
          `${valueToUse} × 7.437e9 = ${result || '2.231e11'}`,
          result || '2.231e11 ch'
        )
      };
    }

    // Astronomical Units to Rods
    if (fromUnit === 'Astronomical Units' && toUnit === 'Rods') {
      return {
        introduction: [
          "Converting from astronomical units to rods.",
          "Astronomical to surveying measurement."
        ],
        formula: {
          main: "rd = au × 2.975e10",
          alternate: "au = rd ÷ 2.975e10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'rd',
          'rd = au × 2.975e10',
          `${valueToUse} × 2.975e10 = ${result || '8.925e11'}`,
          result || '8.925e11 rd'
        )
      };
    }

    // Astronomical Units to Links
    if (fromUnit === 'Astronomical Units' && toUnit === 'Links') {
      return {
        introduction: [
          "Converting from astronomical units to links.",
          "Astronomical to surveying measurement."
        ],
        formula: {
          main: "li = au × 7.437e11",
          alternate: "au = li ÷ 7.437e11"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'au',
          'li',
          'li = au × 7.437e11',
          `${valueToUse} × 7.437e11 = ${result || '2.231e13'}`,
          result || '2.231e13 li'
        )
      };
    }


    // Angstroms to Meters
    if (fromUnit === 'Angstroms' && toUnit === 'Meters') {
      return {
        introduction: [
          "Converting from angstroms to meters.",
          "Atomic scale to metric conversion."
        ],
        formula: {
          main: "m = Å × 1e-10",
          alternate: "Å = m × 1e10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'm',
          'm = Å × 1e-10',
          `${valueToUse} × 1e-10 = ${result || '3e-9'}`,
          result || '3e-9 m'
        )
      };
    }

    // Angstroms to Centimeters
    if (fromUnit === 'Angstroms' && toUnit === 'Centimeters') {
      return {
        introduction: [
          "Converting from angstroms to centimeters.",
          "Atomic scale to metric conversion."
        ],
        formula: {
          main: "cm = Å × 1e-8",
          alternate: "Å = cm × 1e8"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'cm',
          'cm = Å × 1e-8',
          `${valueToUse} × 1e-8 = ${result || '3e-7'}`,
          result || '3e-7 cm'
        )
      };
    }

    // Angstroms to Kilometers
    if (fromUnit === 'Angstroms' && toUnit === 'Kilometers') {
      return {
        introduction: [
          "Converting from angstroms to kilometers.",
          "Atomic scale to metric conversion."
        ],
        formula: {
          main: "km = Å × 1e-13",
          alternate: "Å = km × 1e13"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'km',
          'km = Å × 1e-13',
          `${valueToUse} × 1e-13 = ${result || '3e-12'}`,
          result || '3e-12 km'
        )
      };
    }

    // Angstroms to Inches
    if (fromUnit === 'Angstroms' && toUnit === 'Inches') {
      return {
        introduction: [
          "Converting from angstroms to inches.",
          "Atomic scale to imperial conversion."
        ],
        formula: {
          main: "in = Å × 3.937e-9",
          alternate: "Å = in × 2.54e8"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'in',
          'in = Å × 3.937e-9',
          `${valueToUse} × 3.937e-9 = ${result || '1.181e-7'}`,
          result || '1.181e-7 in'
        )
      };
    }

    // Angstroms to Feet
    if (fromUnit === 'Angstroms' && toUnit === 'Feet') {
      return {
        introduction: [
          "Converting from angstroms to feet.",
          "Atomic scale to imperial conversion."
        ],
        formula: {
          main: "ft = Å × 3.281e-10",
          alternate: "Å = ft × 3.048e9"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'ft',
          'ft = Å × 3.281e-10',
          `${valueToUse} × 3.281e-10 = ${result || '9.843e-9'}`,
          result || '9.843e-9 ft'
        )
      };
    }

    // Angstroms to Yards
    if (fromUnit === 'Angstroms' && toUnit === 'Yards') {
      return {
        introduction: [
          "Converting from angstroms to yards.",
          "Atomic scale to imperial conversion."
        ],
        formula: {
          main: "yd = Å × 1.094e-10",
          alternate: "Å = yd × 9.144e9"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'yd',
          'yd = Å × 1.094e-10',
          `${valueToUse} × 1.094e-10 = ${result || '3.281e-9'}`,
          result || '3.281e-9 yd'
        )
      };
    }

    // Angstroms to Miles
    if (fromUnit === 'Angstroms' && toUnit === 'Miles') {
      return {
        introduction: [
          "Converting from angstroms to miles.",
          "Atomic scale to imperial conversion."
        ],
        formula: {
          main: "mi = Å × 6.214e-14",
          alternate: "Å = mi × 1.609e13"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'mi',
          'mi = Å × 6.214e-14',
          `${valueToUse} × 6.214e-14 = ${result || '1.864e-12'}`,
          result || '1.864e-12 mi'
        )
      };
    }

    // Angstroms to Nautical Miles
    if (fromUnit === 'Angstroms' && toUnit === 'Nautical Miles') {
      return {
        introduction: [
          "Converting from angstroms to nautical miles.",
          "Atomic scale to maritime measurement."
        ],
        formula: {
          main: "nmi = Å × 5.396e-14",
          alternate: "Å = nmi × 1.852e13"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'nmi',
          'nmi = Å × 5.396e-14',
          `${valueToUse} × 5.396e-14 = ${result || '1.619e-12'}`,
          result || '1.619e-12 nmi'
        )
      };
    }

    // Angstroms to Light Years
    if (fromUnit === 'Angstroms' && toUnit === 'Light Years') {
      return {
        introduction: [
          "Converting from angstroms to light years.",
          "Atomic scale to astronomical measurement."
        ],
        formula: {
          main: "ly = Å × 1.057e-26",
          alternate: "Å = ly × 9.461e25"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'ly',
          'ly = Å × 1.057e-26',
          `${valueToUse} × 1.057e-26 = ${result || '3.171e-25'}`,
          result || '3.171e-25 ly'
        )
      };
    }

    // Angstroms to Parsecs
    if (fromUnit === 'Astronomical Units' && toUnit === 'Parsecs') {
      return {
        introduction: [
          "Converting from angstroms to parsecs.",
          "Atomic scale to astronomical measurement."
        ],
        formula: {
          main: "pc = Å × 3.24e-27",
          alternate: "Å = pc × 3.086e26"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'pc',
          'pc = Å × 3.24e-27',
          `${valueToUse} × 3.24e-27 = ${result || '9.72e-26'}`,
          result || '9.72e-26 pc'
        )
      };
    }

    // Angstroms to Astronomical Units
    if (fromUnit === 'Angstroms' && toUnit === 'Astronomical Units') {
      return {
        introduction: [
          "Converting from angstroms to astronomical units.",
          "Atomic scale to astronomical measurement."
        ],
        formula: {
          main: "au = Å × 6.684e-22",
          alternate: "Å = au × 1.496e21"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'au',
          'au = Å × 6.684e-22',
          `${valueToUse} × 6.684e-22 = ${result || '2.005e-20'}`,
          result || '2.005e-20 au'
        )
      };
    }

    // Angstroms to Microns
    if (fromUnit === 'Angstroms' && toUnit === 'Microns') {
      return {
        introduction: [
          "Converting from angstroms to microns.",
          "Atomic scale to microscopic measurement."
        ],
        formula: {
          main: "μ = Å × 1e-4",
          alternate: "Å = μ × 1e4"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'μ',
          'μ = Å × 1e-4',
          `${valueToUse} × 1e-4 = ${result || '3e-3'}`,
          result || '3e-3 μ'
        )
      };
    }

    // Angstroms to Mils
    if (fromUnit === 'Angstroms' && toUnit === 'Mils') {
      return {
        introduction: [
          "Converting from angstroms to mils.",
          "Atomic scale to engineering measurement."
        ],
        formula: {
          main: "mil = Å × 3.937e-7",
          alternate: "Å = mil × 2.54e6"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'mil',
          'mil = Å × 3.937e-7',
          `${valueToUse} × 3.937e-7 = ${result || '1.181e-5'}`,
          result || '1.181e-5 mil'
        )
      };
    }

    // Angstroms to Fathoms
    if (fromUnit === 'Angstroms' && toUnit === 'Fathoms') {
      return {
        introduction: [
          "Converting from angstroms to fathoms.",
          "Atomic scale to maritime measurement."
        ],
        formula: {
          main: "ftm = Å × 5.468e-11",
          alternate: "Å = ftm × 1.829e10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'ftm',
          'ftm = Å × 5.468e-11',
          `${valueToUse} × 5.468e-11 = ${result || '1.640e-9'}`,
          result || '1.640e-9 ftm'
        )
      };
    }

    // Angstroms to Furlongs
    if (fromUnit === 'Angstroms' && toUnit === 'Furlongs') {
      return {
        introduction: [
          "Converting from angstroms to furlongs.",
          "Atomic scale to historical measurement."
        ],
        formula: {
          main: "fur = Å × 4.971e-13",
          alternate: "Å = fur × 2.012e12"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'fur',
          'fur = Å × 4.971e-13',
          `${valueToUse} × 4.971e-13 = ${result || '1.491e-11'}`,
          result || '1.491e-11 fur'
        )
      };
    }

    // Angstroms to Chains
    if (fromUnit === 'Angstroms' && toUnit === 'Chains') {
      return {
        introduction: [
          "Converting from angstroms to chains.",
          "Atomic scale to surveying measurement."
        ],
        formula: {
          main: "ch = Å × 4.971e-12",
          alternate: "Å = ch × 2.012e11"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'ch',
          'ch = Å × 4.971e-12',
          `${valueToUse} × 4.971e-12 = ${result || '1.491e-10'}`,
          result || '1.491e-10 ch'
        )
      };
    }

    // Angstroms to Rods
    if (fromUnit === 'Angstroms' && toUnit === 'Rods') {
      return {
        introduction: [
          "Converting from angstroms to rods.",
          "Atomic scale to surveying measurement."
        ],
        formula: {
          main: "rd = Å × 1.988e-11",
          alternate: "Å = rd × 5.029e10"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'rd',
          'rd = Å × 1.988e-11',
          `${valueToUse} × 1.988e-11 = ${result || '5.964e-10'}`,
          result || '5.964e-10 rd'
        )
      };
    }

    // Angstroms to Links
    if (fromUnit === 'Angstroms' && toUnit === 'Links') {
      return {
        introduction: [
          "Converting from angstroms to links.",
          "Atomic scale to surveying measurement."
        ],
        formula: {
          main: "li = Å × 4.971e-10",
          alternate: "Å = li × 2.012e9"
        },
        conversionSteps: formatConversionSteps(
          valueToUse,
          'Å',
          'li',
          'li = Å × 4.971e-10',
          `${valueToUse} × 4.971e-10 = ${result || '1.491e-8'}`,
          result || '1.491e-8 li'
        )
      };
    }
    // Default case for other conversions
    return {
      introduction: [
        `Converting between ${fromUnit} and ${toUnit} involves using a specific conversion factor.`,
        "Let's break down this conversion step by step."
      ],
      formula: `${toUnit} = ${fromUnit} × [conversion factor]`,
      conversionSteps: [
        `Start with ${inputValue} ${fromUnit}`,
        `Apply conversion factor`,
        `Result: ${result} ${toUnit}`
      ]
    };
  };

  const conversionData = getConversionData();

  return (
    <UnifiedSimpleConversionExplanation
      conversionType="length"
      fromUnit={fromUnit}
      toUnit={toUnit}
      inputValue={inputValue}
      result={result}
      darkMode={darkMode}
      {...conversionData}
    />
  );
};

export default LengthConversionExplanation; 